import React, { useState, useRef, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useAuth } from "../context/AuthContext";
import {
  FaFilePdf, FaFileWord, FaEye, FaEdit,
  FaUndo, FaSave, FaMagic, FaCheckCircle,
  FaFileAlt, FaPrint, FaExpand, FaCompress,
  FaBold, FaItalic, FaUnderline, FaRedo,
  FaStrikethrough, FaSuperscript, FaSubscript,
  FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify,
  FaListUl, FaListOl, FaIndent, FaOutdent,
  FaLink, FaUnlink, FaEraser, FaHighlighter, FaPaintBrush,
  FaClipboardList, FaTimes, FaCalculator
} from "react-icons/fa";
import styles from "./OfferLetterEditor.module.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import useOfferLetterAccess from "../hooks/useOfferLetterAccess";
import { computeGrossBreakdown, sumGrossBreakdown, computeTotalCTC, splitRetentionTranches } from "../utils/salaryUtils";

// ─────────────────────────────────────────────
// Default offer letter HTML template
// ─────────────────────────────────────────────
/* shared cell style helpers baked inline – Quill strips border="" and cellpadding="" HTML attrs */
const TD   = 'padding:8px 10px;border:1px solid #aab8cc;vertical-align:top;word-break:break-word;overflow-wrap:break-word;';
const TDC  = 'padding:8px 10px;border:1px solid #aab8cc;vertical-align:top;background:#f0f4ff;word-break:break-word;overflow-wrap:break-word;';
const TDR  = 'padding:8px 10px;border:1px solid #aab8cc;vertical-align:middle;text-align:right;white-space:nowrap;';
const TDRC = 'padding:8px 10px;border:1px solid #aab8cc;vertical-align:middle;text-align:right;white-space:nowrap;background:#f0f4ff;';
const TH   = 'padding:9px 10px;border:1px solid #1a3c6e;text-align:left;font-weight:bold;';
const THR  = 'padding:9px 10px;border:1px solid #1a3c6e;text-align:right;font-weight:bold;';
const TDLBL= 'width:38%;padding:8px 10px;border:1px solid #aab8cc;font-weight:bold;vertical-align:top;';
const TDLBLC='width:38%;padding:8px 10px;border:1px solid #aab8cc;font-weight:bold;vertical-align:top;background:#f0f4ff;';
const TDTOT = 'padding:8px 10px;border:1px solid #0d2a52;font-weight:bold;text-align:right;white-space:nowrap;';
const TDTOT2= 'padding:8px 10px;border:1px solid #264e82;font-weight:bold;text-align:right;white-space:nowrap;';
// Amount-column variants without white-space:nowrap (for fixed-layout tables where column widths are explicit)
const TDRA  = 'padding:8px 6px;border:1px solid #aab8cc;vertical-align:middle;text-align:right;';
const TDRCA = 'padding:8px 6px;border:1px solid #aab8cc;vertical-align:middle;text-align:right;background:#f0f4ff;';

// ─────────────────────────────────────────────
// Salary Computation Utilities
// Fixed-breakdown model (matches Employee Portal): Basic/HRA/LTA/Medical/
// Telephone/Special are computed by the shared gross calculator from a
// target Monthly Gross. Joining Bonus, Variable Pay Pool and Retention
// Bonus Pool are always entered manually by HR — no CTC-tier formula.
// Total CTC = Annual Gross + Variable Pool + Retention Pool + Joining Bonus.
// ─────────────────────────────────────────────

/** Extract numeric CTC from strings like "₹8,00,000/-", "8,00,000", "800000" */
const parseCTCNumber = (str = '') => {
  const cleaned = str.replace(/[₹,\s\-/]/g, '').replace(/[^0-9].*/,'');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
};

/** Round to 2 decimal places to preserve sub-rupee precision */
const r2 = (n) => Math.round(n * 100) / 100;

/** Format number in Indian numbering system with up to 2 decimal places */
const fmtINR = (n) => { const v = r2(n); return v.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 }); };

/** Format a fraction (0–1) as a percentage string, preserving decimals (e.g. 0.015 → "1.5%", 0.04 → "4%") */
const pctFmt = (f) => { const v = f * 100; return (v % 1 === 0 ? v.toString() : v.toFixed(1)) + '%'; };

/** Convert a number to Indian English words (e.g. 800000 → "Eight Lakhs") */
const numToWordsINR = (n) => {
  if (!n || n === 0) return 'Zero';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
                 'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen',
                 'Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const twoD = (x) => x < 20 ? ones[x] : tens[Math.floor(x/10)] + (x%10 ? ' '+ones[x%10] : '');
  let num = Math.round(n);
  const parts = [];
  if (num >= 10000000) { const c = Math.floor(num/10000000); parts.push(twoD(c)+(c>1?' Crores':' Crore')); num %= 10000000; }
  if (num >= 100000)   { const l = Math.floor(num/100000);   parts.push(twoD(l)+(l>1?' Lakhs':' Lakh'));   num %= 100000; }
  if (num >= 1000)     { const t = Math.floor(num/1000);     parts.push(twoD(t)+' Thousand'); num %= 1000; }
  if (num >= 100)      { parts.push(ones[Math.floor(num/100)]+' Hundred'); num %= 100; }
  if (num > 0)         { parts.push(twoD(num)); }
  return parts.join(' ');
};

/** Format numeric CTC into full letter-ready string: ₹X/- (Rupees X Words Only) */
const fmtCTCAmount = (num) => `\u20b9${fmtINR(num)}/- (Rupees ${numToWordsINR(num)} Only)`;

/** Fill-detail keys that drive the salary calculator recompute */
const SALARY_INPUT_KEYS = ['GROSS_INPUT', 'JOINING_BONUS_INPUT', 'VARIABLE_POOL_INPUT', 'RETENTION_POOL_INPUT'];

/**
 * Builds every offer-letter salary token from manually-entered values —
 * mirrors the Employee Portal's fixed-breakdown model (no CTC-tier formula).
 * @param {object} inputs { monthlyGross, joiningBonus, variablePool, retentionPool }
 */
const computeOfferSalary = ({ monthlyGross: grossInput, joiningBonus: joiningBonusInput, variablePool, retentionPool: retentionPoolInput }) => {
  const gross = Number(grossInput) || 0;
  if (gross <= 0) return {};

  const gb = computeGrossBreakdown(gross);
  const { basic, hra, lta, medical, telephone, special } = gb;
  const { monthlyGross, annualGross } = sumGrossBreakdown(gb);
  const joiningBonus  = r2(Number(joiningBonusInput) || 0);
  const annualVariable = r2(Number(variablePool) || 0);
  const retentionPool  = r2(Number(retentionPoolInput) || 0);
  const annualCTC = computeTotalCTC(gb, { joiningBonus, variablePayPool: annualVariable, retentionPool });

  const annualRetention = r2(retentionPool / 5);  // per-chunk (pool ÷ 5)

  // Bond breach: fixed full-recovery amount (no pro-rata reductions permitted)
  const bondBreach    = r2(monthlyGross * 3); // Clause 6.3: Bond Breach (Training & Content Indemnity) — 3 months Gross Monthly Salary (full amount)
  const hasVariable   = annualVariable > 0;
  const hasRetention  = retentionPool > 0;

  // Retention Bonus: fixed committed pool (fully inside CTC) | disbursed in 5 equal annual cash chunks
  const { t1: retentionT1, t2: retentionT2, t3: retentionT3, t4: retentionT4, t5: retentionT5 } = splitRetentionTranches(retentionPool);

  const NA_BAND = 'Not Applicable';

  return {
    // ── Fixed pay table tokens (plain numbers, no ₹) ──────────────────────────
    S_BASIC_M:  fmtINR(basic),        S_BASIC_A:  fmtINR(basic * 12),
    S_HRA_M:    fmtINR(hra),          S_HRA_A:    fmtINR(hra * 12),
    S_LTA_M:    fmtINR(lta),          S_LTA_A:    fmtINR(lta * 12),
    S_MED_M:    fmtINR(medical),      S_MED_A:    fmtINR(medical * 12),
    S_TEL_M:    fmtINR(telephone),    S_TEL_A:    fmtINR(telephone * 12),
    S_SPEC_M:   fmtINR(special),      S_SPEC_A:   fmtINR(special * 12),
    S_GROSS_M:  fmtINR(monthlyGross), S_GROSS_A:  fmtINR(annualGross),
    S_CTC1_A:   fmtINR(annualCTC),    S_RELOC_A:  fmtINR(joiningBonus),
    // ── Variable pay tokens ────────────────────────────────────────────────────
    S_VARIABLE_M:    hasVariable ? '\u2014' : '\u2014',   // not paid monthly
    S_VARIABLE_A:    hasVariable ? fmtINR(annualVariable) : '\u2014',
    S_VARIABLE_PCT:  hasVariable && annualCTC > 0 ? pctFmt(annualVariable / annualCTC) : '\u2014',
    S_VARIABLE_TAX:  hasVariable ? 'Fully taxable as salary income u/s 15 of IT Act, 1961; TDS deducted by employer in the month of release' : '\u2014',
    S_VARIABLE_NOTE: hasVariable
      ? `Pool of \u20b9${fmtINR(annualVariable)}/- committed in CTC at time of appointment. Paid once a year at end of financial year (April). Actual amount released = Pool \u00d7 Performance KPI% (as set by HR/management based on annual review). Performance KPI is set to 0 at the time of joining and is adjusted after the first annual review cycle; disbursement for the joining year is accordingly \u20b90/-. CTC declares the full maximum pool \u2014 actual disbursement may be less. Subject to continued employment on 31 March of that financial year. Fully taxable u/s 15 IT Act, 1961.`
      : `Not Applicable \u2013 Variable Pay does not apply for this appointment.`,
    // ── ESOP tokens ────────────────────────────────────────────────────────────
    S_ESOP_A:    hasRetention ? fmtINR(annualRetention) : '—',
    S_ESOP_POOL: hasRetention ? fmtINR(retentionPool)   : '—',
    S_ESOP_T1:   hasRetention ? fmtINR(retentionT1)     : 'N/A',
    S_ESOP_T2:   hasRetention ? fmtINR(retentionT2)     : 'N/A',
    S_ESOP_T3:   hasRetention ? fmtINR(retentionT3)     : 'N/A',
    S_ESOP_T4:   hasRetention ? fmtINR(retentionT4)     : 'N/A',
    S_ESOP_T5:   hasRetention ? fmtINR(retentionT5)     : 'N/A',
    S_ESOP_PCT:  hasRetention && annualCTC > 0 ? pctFmt(retentionPool / annualCTC) : '—',
    S_ESOP_TAX:  hasRetention ? 'Taxable as salary income u/s 15 of IT Act, 1961; TDS deducted by employer at source' : '—',
    S_ESOP_NOTE: hasRetention
      ? `Pool of \u20b9${fmtINR(retentionPool)}/- committed in CTC; disbursed in 5 equal cash chunks of \u20b9${fmtINR(retentionT1)}/- each (20% of pool per chunk) \u2014 Chunk 1 at end of Year 1, Chunk 2 at end of Year 2, Chunk 3 at end of Year 3, Chunk 4 at end of Year 4, Chunk 5 at end of Year 5. Not KPI-linked. Subject to continued employment on each anniversary date.`
      : NA_BAND,
    S_ESOP_TABLE: hasRetention
      ? `<table style="width:100%;border-collapse:collapse;margin:6px 0 10px 0;font-size:10.5pt;"><thead style="background:#1a3c6e;color:#fff;"><tr><th style="${TH};width:12%;text-align:center;">Chunk</th><th style="${TH};width:38%;">Payment Event</th><th style="${THR};width:18%;">Amount (₹)</th><th style="${THR};width:10%;">Pool %</th><th style="${TH};">Tax Treatment</th></tr></thead><tbody><tr><td style="${TDC};text-align:center;">1</td><td style="${TDC};">End of Year 1 – 12 months from joining date</td><td style="${TDRC};">₹${fmtINR(retentionT1)}/-</td><td style="${TDRC};">20%</td><td style="${TDC};font-size:10pt;">Salary income u/s 15 IT Act; TDS by employer</td></tr><tr><td style="${TD};text-align:center;">2</td><td style="${TD};">End of Year 2 – 24 months from joining date</td><td style="${TDR};">₹${fmtINR(retentionT2)}/-</td><td style="${TDR};">20%</td><td style="${TD};font-size:10pt;">Salary income u/s 15 IT Act; TDS by employer</td></tr><tr><td style="${TDC};text-align:center;">3</td><td style="${TDC};">End of Year 3 – 36 months from joining date</td><td style="${TDRC};">₹${fmtINR(retentionT3)}/-</td><td style="${TDRC};">20%</td><td style="${TDC};font-size:10pt;">Salary income u/s 15 IT Act; TDS by employer</td></tr><tr><td style="${TD};text-align:center;">4</td><td style="${TD};">End of Year 4 – 48 months from joining date</td><td style="${TDR};">₹${fmtINR(retentionT4)}/-</td><td style="${TDR};">20%</td><td style="${TD};font-size:10pt;">Salary income u/s 15 IT Act; TDS by employer</td></tr><tr><td style="${TDC};text-align:center;">5</td><td style="${TDC};">End of Year 5 – 60 months from joining date</td><td style="${TDRC};">₹${fmtINR(retentionT5)}/-</td><td style="${TDRC};">20%</td><td style="${TDC};font-size:10pt;">Salary income u/s 15 IT Act; TDS by employer</td></tr><tr style="background:#1a3c6e;color:#fff;"><td colspan="2" style="${TDTOT};text-align:left;">Total Pool (5 Chunks)</td><td style="${TDTOT};">₹${fmtINR(retentionPool)}/-</td><td style="${TDTOT};">100%</td><td style="padding:8px 10px;border:1px solid #0d2a52;"></td></tr></tbody></table><p style="font-size:10pt;color:#555;margin:4px 0 8px 0;"><em>Cash paid directly to salary account. Each chunk released only on completion of that year of service. Full pool is committed in CTC from day one. A Retention Bonus Commitment Letter will be issued separately.</em></p>`
      : `<p style="font-size:10pt;color:#9ca3af;font-style:italic;margin:4px 0 8px 0;">Not applicable for this appointment.</p>`,
    // ── Display-label tokens (used in "Understanding Your CTC" employee guide box) ──
    S_VAR_AMT_LABEL:   hasVariable ? `\u20b9${fmtINR(annualVariable)}/yr pool (performance-based)` : 'Not applicable for this appointment',
    S_VAR_WHEN_LABEL:  hasVariable
      ? `Paid <strong>once a year at end of financial year (April)</strong> based on annual KPI review by HR/management. Actual release = Pool \u00d7 Performance KPI% (as determined by annual review). Not paid monthly \u2014 not reflected in regular payslips. Subject to continued employment through 31 March of that financial year. Fully taxable u/s 15 IT Act.`
      : 'Not Applicable \u2013 Variable Pay does not apply for this appointment.',
    S_VAR_BLOCK_LINE:  hasVariable
      ? `\u20b9${fmtINR(annualVariable)}/yr pool (performance-based; actual = KPI% \u00d7 pool; paid end of financial year, April)`
      : 'Not Applicable',
    S_ESOP_AMT_LABEL:  hasRetention
      ? `\u20b9${fmtINR(retentionPool)} pool (5 equal chunks of \u20b9${fmtINR(retentionT1)} each)`
      : 'Not applicable for this appointment',
    S_ESOP_WHEN_SHORT: hasRetention
      ? `Chunk 1 (end of Yr 1): \u20b9${fmtINR(retentionT1)} | Chunk 2 (end of Yr 2): \u20b9${fmtINR(retentionT2)} | Chunk 3 (end of Yr 3): \u20b9${fmtINR(retentionT3)} | Chunk 4 (end of Yr 4): \u20b9${fmtINR(retentionT4)} | Chunk 5 (end of Yr 5): \u20b9${fmtINR(retentionT5)}`
      : 'Not Applicable',
    S_ESOP_BLOCK_LINE: hasRetention
      ? `\u20b9${fmtINR(retentionPool)} (5 equal chunks of \u20b9${fmtINR(retentionT1)} each)`
      : 'Not Applicable',
    // ── Narrative tokens (\u20b9-formatted; used in letter prose) ───────────────────
    MONTHLY_GROSS:      `\u20b9${fmtINR(monthlyGross)}/-`,
    // ── ESIC applicability — computed from actual gross, not a hardcoded claim ──
    // Wage ceiling under ESIC Act, 1948 is \u20b921,000/month (\u20b925,000 for persons with disability).
    S_ESIC_NOTE: monthlyGross < 21000
      ? `The gross monthly salary for this role is \u20b9${fmtINR(monthlyGross)}/-, which is at or below the \u20b921,000/month wage ceiling under the ESIC Act, 1948. ESIC coverage is therefore <strong>applicable</strong> for this position (subject to the Company's ESIC registration status and applicable employee-count threshold). Employer and employee ESIC contributions shall be deducted/contributed as per prevailing ESIC rates.`
      : `The gross monthly salary for this role (\u20b9${fmtINR(monthlyGross)}/-) exceeds the \u20b921,000/month wage ceiling under the ESIC Act, 1948, and therefore ESIC coverage is <strong>not applicable</strong> for this position. If this threshold is revised by the Government, or if a subsequent role/revision brings the salary at or below the threshold, ESIC applicability shall be reassessed accordingly.`,
    // Short form for the Annexure A.3 statutory summary table
    S_ESIC_STATUS: monthlyGross < 21000
      ? `<strong>APPLICABLE</strong> for this role (Gross salary \u20b9${fmtINR(monthlyGross)}/- is at or below the \u20b921,000/month ESIC wage ceiling)`
      : `<strong>NOT APPLICABLE</strong> for this role (Gross salary \u20b9${fmtINR(monthlyGross)}/- exceeds the \u20b921,000/month ESIC wage ceiling)`,
    ANNUAL_RECURRING:   `\u20b9${fmtINR(annualGross)}/-`,
    BOND_BREACH_AMOUNT: `3 × ₹${fmtINR(monthlyGross)}/- (Gross Monthly Salary) = ₹${fmtINR(bondBreach)}/- (Full amount payable on breach)`,
    JOINING_BONUS_AMT:  fmtCTCAmount(joiningBonus),
    // ── Professional display tokens – replaces raw '₹—/-' in prose/table contexts ──
    S_VARIABLE_AMT_INNER: hasVariable
      ? `<strong>\u20b9${fmtINR(annualVariable)}</strong><br/><span style="font-size:9pt;color:#374151;">(max pool)</span>`
      : `<em style="font-size:9.5pt;color:#6b7280;">Not applicable for this appointment</em>`,
    S_ESOP_POOL_INNER: hasRetention
      ? `<strong>\u20b9${fmtINR(retentionPool)}</strong><br/><span style="font-size:9pt;color:#374151;">(full amount)</span>`
      : `<em style="font-size:9.5pt;color:#6b7280;">Not applicable for this appointment</em>`,
    S_VARIABLE_POOL_CELL: hasVariable
      ? `\u20b9${fmtINR(annualVariable)}/- per year`
      : 'Not applicable for this appointment',
    S_VARIABLE_APPLIC_CELL: hasVariable
      ? `Included in this appointment. Committed maximum pool: \u20b9${fmtINR(annualVariable)}/- per year. Actual release = Pool \u00d7 Performance KPI% as determined by HR\u2009/\u2009management after annual review. Note: Performance KPI defaults to 0 at joining; variable pay for the first year will be \u20b90/- and is subject to the first annual review cycle.`
      : 'Variable Pay is not included in this appointment.',
    S_ESOP_POOL_ANNEX: hasRetention
      ? `\u20b9${fmtINR(retentionPool)}/- (full pool)`
      : 'Not applicable for this appointment',
  };
};

const DEFAULT_TEMPLATE = `<div style="font-family:'Times New Roman',serif;font-size:12pt;color:#1a1a1a;line-height:1.8;max-width:900px;margin:0 auto;">

  <!-- HEADER -->
  <div style="border:2px solid #1a3c6e;padding:18px 22px 16px 22px;margin-bottom:0;">
    <div style="display:flex;align-items:center;gap:18px;margin-bottom:10px;">
      <img src="/logo.jpg" alt="PrrepX Pathshala Logo" style="width:90px;height:90px;object-fit:contain;flex-shrink:0;" />
      <h1 style="margin:0;font-size:17pt;color:#1a3c6e;letter-spacing:1.5px;font-weight:900;line-height:1.2;">PRREPX PATHSHALA PRIVATE LIMITED <span style="color:#aaa;font-size:8pt;">&nbsp;(CIN: U85306UP2025PTC238181)</span></h1>
    </div>
    <p style="margin:0 0 6px 0;font-weight:bold;font-size:11pt;">ET 15/302 EWS LIG, DLF Garden, Mohanlalganj, Lucknow, Uttar Pradesh – 226301</p>
    <div style="display:flex;flex-wrap:wrap;gap:14px;align-items:center;font-size:10pt;">
      <span>🌐 https://prrepxpathshala.live</span>
      <span style="color:#bbb;">|</span>
      <span>✉️ admin@prrepxpathshala.live</span>
      <span style="color:#bbb;">|</span>
      <span>📞 +91 9266517468</span>
    </div>
  </div>
  <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:22px;padding:8px 4px 0 4px;">
    <span style="color:#8b0000;font-weight:bold;letter-spacing:2px;font-size:10pt;">OFFER LETTER</span>
    <span style="color:#8b0000;font-weight:bold;letter-spacing:3px;font-size:9pt;">STRICTLY PRIVATE &amp; CONFIDENTIAL</span>
  </div>

  <p><strong>Date:</strong> {{OFFER_DATE}}</p>
  <p><strong>Offer Letter Reference No.:</strong> {{REF_NUMBER}}</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <p><strong>To,</strong></p>
  <p><strong>{{EMPLOYEE_NAME}}</strong><br/>
  {{EMPLOYEE_ADDRESS}}<br/>
  <strong>Email:</strong> {{EMPLOYEE_EMAIL}}<br/>
  <strong>Mobile:</strong> {{EMPLOYEE_MOBILE}}</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 style="color:#1a3c6e;">Dear {{FIRST_NAME}},</h2>
  <p>We are delighted to extend this offer of employment to you at <strong>PrrepX Pathshala Private Limited</strong> ("the Company" / "PrrepX Pathshala"). Following our rounds of assessments and discussions, we are pleased to welcome you as a valued member of our growing team.</p>
  <p>At PrrepX Pathshala, our mission is to democratize high-quality education for students across India — from Class 6–12 learners to IIT-JEE and NEET aspirants. We believe that the right teacher and the right platform can fundamentally transform a student's future. We are excited to have you contribute to that mission.</p>
  <p>This letter sets out the terms and conditions of your employment with the Company. Please read this document carefully and in its entirety before signing. This offer is contingent upon the terms mentioned herein and subject to your acceptance by the deadline indicated.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">1. DESIGNATION &amp; REPORTING STRUCTURE</h2>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <tr><td style="${TDLBLC}">Designation / Job Title</td><td style="${TDC}">{{DESIGNATION}}</td></tr>
    <tr><td style="${TDLBL}">Department</td><td style="${TD}">{{DEPARTMENT}}</td></tr>
    <tr><td style="${TDLBLC}">Grade / Level</td><td style="${TDC}">{{GRADE_LEVEL}}</td></tr>
    <tr><td style="${TDLBL}">Reporting To</td><td style="${TD}">{{REPORTING_TO}}</td></tr>
    <tr><td style="${TDLBLC}">Employment ID</td><td style="${TDC}">To be allotted on or before your date of joining</td></tr>
  </table>
  <p>Your primary responsibilities shall include but are not limited to those described in the <strong>Job Description</strong> communicated to you during the interview process. The Company reserves the right to revise, add, or reassign responsibilities as per organizational requirements, provided such changes are communicated to you with reasonable notice. A detailed KRA (Key Result Areas) document shall be shared with you at or before joining.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">2. DATE OF JOINING &amp; WORK LOCATION</h2>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <tr><td style="${TDLBLC}">Date of Joining</td><td style="${TDC}">{{DATE_OF_JOINING}}</td></tr>
    <tr><td style="${TDLBL}">Primary Work Location</td><td style="${TD}">{{WORK_LOCATION}}</td></tr>
    <tr><td style="${TDLBLC}">Mode of Work</td><td style="${TDC}">On-site (Full-Time)</td></tr>
  </table>
  <p>The Company may, at its discretion, require you to work from any other office, city, or location in India as per business requirements. Reasonable advance notice and applicable relocation support shall be provided in such cases, subject to the Company's relocation policy in effect at that time.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">3. COMPENSATION &amp; BENEFITS</h2>

  <h3 style="color:#2c5282;margin-top:12px;">3.1 Total Cost to Company (CTC)</h3>
  <p>Your Total <strong>Cost to Company (CTC)</strong> is <strong>{{CTC_AMOUNT}}</strong>. CTC is the complete commitment the Company makes at the time of your appointment &mdash; it covers your regular monthly salary, the committed Variable Pay pool (performance-linked, paid annually at end of financial year), the committed Retention Bonus pool (tenure-linked, paid in 5 chunks), and the one-time Joining Bonus. It is a complete picture of what the Company commits for you, whether paid every month or over time.</p>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;color:#2d3748;">
    <strong>Note:</strong> CTC is the total expenditure committed by the Company on your employment, covering all pay components. CTC is <strong>not</strong> your take-home salary &mdash; your actual in-hand amount will be lower after Income Tax (TDS), Professional Tax, and any other applicable statutory deductions.
  </blockquote>

  <h3 style="color:#2c5282;margin-top:12px;">3.2 Salary Structure &amp; CTC Breakup</h3>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;table-layout:fixed;">
    <colgroup>
      <col style="width:26px;"/>
      <col style="width:24%;"/>
      <col style="width:11%;"/>
      <col style="width:11%;"/>
      <col/>
    </colgroup>
    <thead style="background:#1a3c6e;color:#fff;">
      <tr>
        <th style="${TH};text-align:center;padding:8px 4px;white-space:nowrap;">#</th>
        <th style="${TH};">Component</th>
        <th style="${THR};white-space:nowrap;">Monthly<br/>(₹)</th>
        <th style="${THR};white-space:nowrap;">Annual /<br/>Total (₹)</th>
        <th style="${TH};">Remarks</th>
      </tr>
    </thead>
    <tbody>
      <tr style="background:#dbeafe;">
        <td colspan="5" style="padding:6px 14px;font-weight:700;font-size:10pt;color:#1e3a5f;border:1px solid #93c5fd;">&#9312;&nbsp; MONTHLY SALARY</td>
      </tr>
      <tr>
        <td style="${TDC};text-align:center;padding:8px 4px;">1</td>
        <td style="${TDC}"><strong>Basic Salary</strong></td>
        <td style="${TDRCA}">{{S_BASIC_M}}</td>
        <td style="${TDRCA}">{{S_BASIC_A}}</td>
        <td style="${TDC};font-size:9.5pt;">Paid every month. Forms the basis for statutory benefit calculations (Gratuity, EPF when applicable).</td>
      </tr>
      <tr>
        <td style="${TD};text-align:center;padding:8px 4px;">2</td>
        <td style="${TD}"><strong>House Rent Allowance (HRA)</strong></td>
        <td style="${TDRA}">{{S_HRA_M}}</td>
        <td style="${TDRA}">{{S_HRA_A}}</td>
        <td style="${TD};font-size:9.5pt;">Paid every month as part of your salary. Partially tax-exempt u/s 10(13A) if you pay rent &mdash; submit rent receipts/agreement to HR to claim the exemption.</td>
      </tr>
      <tr>
        <td style="${TDC};text-align:center;padding:8px 4px;">3</td>
        <td style="${TDC}"><strong>Leave Travel Allowance (LTA)</strong></td>
        <td style="${TDRCA}">{{S_LTA_M}}</td>
        <td style="${TDRCA}">{{S_LTA_A}}</td>
        <td style="${TDC};font-size:9.5pt;">Paid every month. Claimable as tax-exempt u/s 10(5) for actual domestic travel &mdash; up to twice in a 4-year period; original travel bills/boarding passes required.</td>
      </tr>
      <tr>
        <td style="${TD};text-align:center;padding:8px 4px;">4</td>
        <td style="${TD}"><strong>Medical Allowance</strong></td>
        <td style="${TDRA}">{{S_MED_M}}</td>
        <td style="${TDRA}">{{S_MED_A}}</td>
        <td style="${TD};font-size:9.5pt;">Paid every month as part of your salary. Fully taxable income.</td>
      </tr>
      <tr>
        <td style="${TDC};text-align:center;padding:8px 4px;">5</td>
        <td style="${TDC}"><strong>Telephone &amp; Internet Allowance</strong></td>
        <td style="${TDRCA}">{{S_TEL_M}}</td>
        <td style="${TDRCA}">{{S_TEL_A}}</td>
        <td style="${TDC};font-size:9.5pt;">Paid every month. Submit phone/internet bills to HR to claim tax exemption on actual expenses; amount exceeding submitted bills is taxable.</td>
      </tr>
      <tr>
        <td style="${TD};text-align:center;padding:8px 4px;">6</td>
        <td style="${TD}"><strong>Special Allowance / Flexible Benefit</strong></td>
        <td style="${TDRA}">{{S_SPEC_M}}</td>
        <td style="${TDRA}">{{S_SPEC_A}}</td>
        <td style="${TD};font-size:9.5pt;">Residual variable amount — paid every month. Grows as CTC increases since it absorbs the remainder after all fixed components. Fully taxable. May be restructured under a Flexible Benefit Plan (FBP) at Company&apos;s discretion.</td>
      </tr>
      <tr style="background:#1a3c6e;color:#fff;">
        <td colspan="3" style="padding:8px 10px;border:1px solid #0d2a52;font-weight:bold;text-align:left;">Gross Salary = {{S_GROSS_M}} /mo</td>
        <td style="${TDTOT}">{{S_GROSS_A}}</td>
        <td style="padding:8px 10px;border:1px solid #0d2a52;font-size:9pt;color:#cce;">/yr (monthly &times;12)</td>
      </tr>
      <tr style="background:#dcfce7;">
        <td colspan="5" style="padding:6px 14px;font-weight:700;font-size:10pt;color:#14532d;border:1px solid #86efac;">&#9313;&nbsp; ADDITIONAL COMPENSATION COMPONENTS</td>
      </tr>
      <tr>
        <td style="${TDC};text-align:center;padding:8px 4px;">7</td>
        <td style="${TDC}"><strong>Variable Pay</strong><br/><span style="font-size:9pt;color:#b45309;">(Performance-based, Annual)</span></td>
        <td style="${TDRCA}">&#x2014;</td>
        <td style="${TDRCA}">{{S_VARIABLE_AMT_INNER}}</td>
        <td style="${TDC};font-size:9.5pt;">{{S_VARIABLE_NOTE}}</td>
      </tr>
      <tr>
        <td style="${TD};text-align:center;padding:8px 4px;">8</td>
        <td style="${TD}"><strong>Retention Bonus</strong><br/><span style="font-size:9pt;color:#1d4ed8;">(Fixed, in 5 Chunks)</span></td>
        <td style="${TDRA}">&#x2014;</td>
        <td style="${TDRA}">{{S_ESOP_POOL_INNER}}</td>
        <td style="${TD};font-size:9.5pt;">{{S_ESOP_NOTE}}</td>
      </tr>
      <tr>
        <td style="${TDC};text-align:center;padding:8px 4px;">9</td>
        <td style="${TDC}"><strong>Joining / Relocation Bonus</strong><br/><span style="font-size:9pt;color:#92400e;">(One-time, Year 1 only)</span></td>
        <td style="${TDRCA}">&#x2014;</td>
        <td style="${TDRCA}"><strong>&#x20B9;{{S_RELOC_A}}</strong></td>
        <td style="${TDC};font-size:9.5pt;">Paid once within 30 (Thirty) days of joining; fully taxable; Year 1 only; recoverable if exit within 12 months &mdash; refer Clause 4.</td>
      </tr>
      <tr style="background:#2c5282;color:#fff;">
        <td colspan="3" style="padding:8px 10px;border:1px solid #264e82;font-weight:bold;text-align:left;">Total CTC = Monthly Gross &times;12 + Variable Pay + Retention Bonus + Joining Bonus</td>
        <td style="${TDTOT2}">{{S_CTC1_A}}</td>
        <td style="padding:8px 10px;border:1px solid #264e82;font-size:9pt;color:#cce;">= {{CTC_AMOUNT}}</td>
      </tr>
    </tbody>
  </table>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;">
    <strong>Monthly Gross Salary</strong> = {{MONTHLY_GROSS}}/mo (= {{ANNUAL_RECURRING}}/yr)<br/>
    <strong>Variable Pay</strong> = {{S_VAR_BLOCK_LINE}}<br/>
    <strong>Retention Bonus</strong> = {{S_ESOP_BLOCK_LINE}}<br/>
    <strong>Joining Bonus</strong> = &#x20B9;{{S_RELOC_A}}/- &mdash; one-time, Year 1 only<br/>
    <strong>Total CTC</strong> = {{CTC_AMOUNT}}
  </blockquote>

  <div style="border:2px solid #1a3c6e;border-radius:8px;overflow:hidden;margin:16px 0;font-size:10.5pt;">
    <div style="background:#1a3c6e;color:#fff;padding:10px 16px;">
      <strong style="font-size:11pt;">&#128203; Understanding Your Total Compensation – How Each Part Works</strong>
    </div>
    <div style="padding:10px 16px 6px 16px;background:#eef4ff;">
      <p style="margin:0;color:#1a3c6e;font-size:10.5pt;">Your CTC of <strong>{{CTC_AMOUNT}}</strong> is built from <strong>four distinct components</strong>, each paid at a different time. This table explains each part in plain language so you know exactly what to expect and when.</p>
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:10.5pt;">
      <thead style="background:#2c5282;color:#fff;">
        <tr>
          <th style="${TH};width:20%;">Component</th>
          <th style="${THR};width:17%;">Your Amount</th>
          <th style="${TH};width:35%;">When &amp; How It Is Paid</th>
          <th style="${TH};">Is It Guaranteed?</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style="${TD};"><strong>&#9312; Monthly Salary</strong><br/><span style="font-size:9.5pt;color:#555;">Basic + HRA + LTA + Medical + Tel. + Special Allowance</span></td>
          <td style="${TDR};">{{MONTHLY_GROSS}}/mo<br/><span style="font-size:9pt;color:#777;">= {{ANNUAL_RECURRING}}/yr</span></td>
          <td style="${TD};">Credited to your bank account <strong>every month</strong> on or before the last working day. This is your regular salary – the same amount each month, before tax deductions.</td>
          <td style="${TDC};"><strong style="color:#166534;">&#10004; Fully Guaranteed</strong><br/><span style="font-size:9.5pt;color:#374151;">Paid every month regardless of KPIs or company results. Will not change unless CTC is formally revised.</span></td>
        </tr>
        <tr>
          <td style="${TD};"><strong>&#9313; Variable Pay (Annual Bonus)</strong><br/><span style="font-size:9.5pt;color:#555;">Performance-linked; max pool committed in CTC</span></td>
          <td style="${TDR};">{{S_VAR_AMT_LABEL}}</td>
          <td style="${TD};">{{S_VAR_WHEN_LABEL}}</td>
          <td style="${TDC};"><strong style="color:#b45309;">&#9881; KPI-Linked Performance Bonus</strong><br/><span style="font-size:9.5pt;color:#374151;">CTC declares the full maximum pool. Actual amount released = Pool &times; Performance KPI% (0%–100%) as set by HR/management after annual review.</span></td>
        </tr>
        <tr>
          <td style="${TD};"><strong>&#9314; Retention Bonus (5 Chunks)</strong><br/><span style="font-size:9.5pt;color:#555;">Committed pool; paid in 5 equal cash chunks (not KPI-linked)</span></td>
          <td style="${TDR};">{{S_ESOP_AMT_LABEL}}</td>
          <td style="${TD};">{{S_ESOP_WHEN_SHORT}}<br/><span style="font-size:9.5pt;color:#555;">(Must be employed on each anniversary date. See Section 3.6 for full terms.)</span></td>
          <td style="${TDC};"><strong style="color:#1d4ed8;">&#128274; Tenure-Linked Cash Bonus</strong><br/><span style="font-size:9.5pt;color:#374151;"><strong>Paid as cash to your account.</strong> Full pool committed in CTC from day one, paid in 5 equal cash chunks. Unreleased chunks forfeited if you leave before each anniversary.</span></td>
        </tr>
        <tr>
          <td style="${TDC};"><strong>&#9315; Joining Bonus</strong><br/><span style="font-size:9.5pt;color:#555;">One-time, Year 1 only</span></td>
          <td style="${TDRC};">{{JOINING_BONUS_AMT}}</td>
          <td style="${TDC};">Paid <strong>once</strong>, within 30–60 days of your date of joining. Added to your salary for that month. <strong>Not paid in Year 2</strong> or later – strictly one-time.</td>
          <td style="${TDC};"><strong style="color:#166534;">&#10004; One-Time Guaranteed</strong><br/><span style="font-size:9.5pt;color:#374151;">Paid at joining. However, the <strong>full amount is recoverable</strong> by the Company if you leave within 12 months (see Clause 4).</span></td>
        </tr>
        <tr style="background:#1a3c6e;color:#fff;">
          <td colspan="2" style="${TDTOT};text-align:left;"><strong>TOTAL CTC</strong></td>
          <td colspan="2" style="padding:8px 12px;border:1px solid #0d2a52;font-size:10pt;">Monthly Gross ({{ANNUAL_RECURRING}}/yr) + Variable Pay ({{S_VAR_AMT_LABEL}}) + Retention Bonus ({{S_ESOP_AMT_LABEL}}) + Joining Bonus<br/><strong>= {{CTC_AMOUNT}}</strong></td>
        </tr>
        <tr style="background:#2d4a7a;color:#dce8ff;">
          <td colspan="2" style="${TDTOT2};text-align:left;">From Year 2 Onwards</td>
          <td colspan="2" style="padding:8px 12px;border:1px solid #264e82;font-size:10pt;color:#dce8ff;">Monthly Gross ({{ANNUAL_RECURRING}}/yr) + Variable Pay (KPI-based) + Retention Bonus Chunks 2&ndash;5 + increments per appraisal</td>
        </tr>
      </tbody>
    </table>
    <div style="padding:8px 16px;background:#fef9c3;border-top:1px solid #fbbf24;font-size:10pt;color:#78350f;">
      <strong>Note:</strong> All figures above are <em>pre-tax (gross)</em>. Your actual in-hand salary will be lower after Income Tax (TDS), Professional Tax, and other applicable statutory deductions. Variable Pay is <strong>not received monthly</strong> &ndash; paid as a lump-sum at end of financial year (April) based on KPI set by management. Retention Bonus is <strong>not received monthly</strong> &ndash; each chunk is paid as a lump-sum cash payment (20% of pool) at the end of that year of service.
    </div>
  </div>

  <div style="border:1px solid #d1d5db;border-radius:6px;padding:12px 16px;background:#fffbeb;margin:14px 0;font-size:10.5pt;">
    <p style="margin:0 0 8px 0;font-weight:bold;color:#92400e;">⚠️ Important Disclaimer – Compensation &amp; Deductions</p>
    <ol style="margin:0;padding-left:20px;line-height:1.9;">
      <li><strong>Pre-Tax Figures:</strong> All salary components listed above are <em>pre-tax (gross) amounts</em>. Actual in-hand (net) salary will vary after applicable statutory and regulatory deductions.</li>
      <li><strong>Statutory Deductions:</strong> Deductions such as Income Tax (TDS), Professional Tax, EPF, and ESIC may or may not apply depending on prevailing Government of India regulations, state-specific rules, and the Company's headcount threshold at the time of payment. Applicability is determined solely by law and shall be updated without prior notice when statutory thresholds are crossed.</li>
      <li><strong>Company Policy Adjustments:</strong> The HR department and Company management reserve the right to review, restructure, or adjust the breakup of salary components (e.g., Basic, HRA, allowances) in the future, in accordance with changes in tax laws, CBDT guidelines, labour codes, or internal compensation policy — provided such adjustments do not reduce the agreed gross CTC without mutual written consent.</li>
      <li><strong>No Guarantee of Components:</strong> The individual allowance components (LTA, Medical, Telephone, etc.) are indicative and may be recategorised or merged as part of a Flexible Benefit Plan (FBP) restructuring at the Company's discretion to optimise tax efficiency for the employee.</li>
      <li><strong>Final Authority:</strong> HR / Company Admin is the final authority on payroll computation, deduction applicability, and salary disbursement in the event of any ambiguity.</li>
    </ol>
  </div>

  <h3 style="color:#2c5282;margin-top:12px;">3.3 Notes on Compensation Structure</h3>
  <p><strong>(a) Basic Salary:</strong> The Basic Salary is the foundational, guaranteed fixed pay. It forms the basis for computation of other allowances and statutory benefits such as Gratuity (where applicable under the Payment of Gratuity Act, 1972 upon completion of continuous service of 5 or more years).</p>
  <p><strong>(b) HRA (House Rent Allowance):</strong> HRA is provided as per the Company's compensation structure. Exemption from income tax on HRA is available under Section 10(13A) of the Income Tax Act, 1961, subject to actual rent paid and other conditions prescribed by the Income Tax Department. Employees must submit valid rent receipts/agreements to claim HRA exemption during tax declarations.</p>
  <p><strong>(c) LTA (Leave Travel Allowance):</strong> LTA is reimbursable for domestic travel expenses for you and your immediate family. Exemption may be claimed under Section 10(5) subject to production of valid travel bills and as per the Income Tax rules.</p>
  <p><strong>(d) Special Allowance / Flexible Benefit Plan (FBP):</strong> The Special Allowance is a residual variable-amount component — its value grows as CTC increases since it absorbs the remainder after all fixed components are calculated. Fully taxable. The Company, at its sole discretion and in consultation with the employee, may restructure the Flexible Benefit components to optimize the employee's tax liability in line with prevailing income tax laws and CBDT guidelines. This restructuring shall not reduce the overall CTC without mutual written consent.</p>
  <p><strong>(e) Statutory Deductions from Gross Salary:</strong></p>
  <ul>
    <li><strong>Income Tax (TDS):</strong> Tax Deducted at Source (TDS) will be deducted as per applicable income tax slabs and rates under the Income Tax Act, 1961. Employees must submit their tax-saving investment declarations at the start of each financial year.</li>
    <li><strong>Professional Tax:</strong> Professional Tax (PT) is a state-level tax and will be deducted as applicable in the state of your place of employment.</li>
  </ul>
  <p><strong>(f) Provident Fund (EPF):</strong> The Company currently does not have a mandatory obligation to contribute to the Employees' Provident Fund (EPF) under the Employees' Provident Funds and Miscellaneous Provisions Act, 1952 (EPF Act), as the total number of employees is currently below the statutory threshold of 20 employees. However, the Company fully recognizes its obligation and commits to enrolling all eligible employees in the EPF scheme as soon as the statutory threshold is reached. Upon such enrollment, applicable EPF deductions (Employee's contribution at 12% of Basic Salary) will be made from your salary, and CTC adjustments shall be communicated in advance.</p>
  <p><strong>(g) ESIC:</strong> {{S_ESIC_NOTE}}</p>
  <p><strong>(h) Gratuity:</strong> Gratuity shall be payable as per the Payment of Gratuity Act, 1972, upon completion of a minimum continuous service period of 5 (five) years with the Company, provided the Company meets the applicable employee threshold at the time of separation.</p>

  <h3 style="color:#2c5282;margin-top:12px;">3.4 Salary Payment</h3>
  <ul>
    <li>Salary shall be credited to your designated bank account on or before the <strong>last working day</strong> of each calendar month (or as per payroll cycle in effect at the time).</li>
    <li>You shall be required to open/designate a bank account in a bank of your choosing and submit details prior to joining.</li>
    <li>Salary slips will be issued monthly in digital format through the Company's HR portal or via email.</li>
  </ul>

  <h3 style="color:#2c5282;margin-top:12px;">3.5 Annual Increment &amp; Review</h3>
  <ul>
    <li>Your compensation will be reviewed annually as part of the Company's performance appraisal cycle, typically conducted at the end of each financial year (March–April).</li>
    <li>Increments are performance-linked and discretionary. No increment is guaranteed as a matter of right.</li>
    <li>Any revision in CTC will be communicated via a formal increment/revision letter.</li>
  </ul>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h3 style="color:#2c5282;margin-top:12px;">3.6 Retention Bonus – Detailed Terms</h3>
  <p style="color:#374151;font-size:10.5pt;">This section sets out the formal and plain-language terms for your Retention Bonus. The Retention Bonus is a <strong>pre-committed cash pool</strong> forming part of your total CTC from day one — it is <em>not</em> KPI-linked and is disbursed in <strong>5 equal cash chunks</strong> over 5 years of service, subject only to continued employment on each anniversary date.</p>

  <p><strong>Retention Bonus (5 Equal Cash Chunks)</strong></p>
  <div style="border-left:4px solid #3b82f6;padding:10px 14px;background:#eff6ff;margin:4px 0 10px 0;font-size:10.5pt;line-height:1.8;">
    <p style="margin:0 0 6px 0;"><strong>What it is:</strong> A <em>cash pool</em> fully committed in your CTC at the time of appointment and disbursed in <strong>5 equal cash chunks</strong> over your first 5 years of service. It is designed to reward long-term commitment and loyalty. Each chunk is released at the end of that year of continued employment and credited directly to your bank account.</p>
    <p style="margin:0 0 6px 0;"><strong>Not KPI-linked:</strong> Unlike a performance bonus, this is <strong>not</strong> linked to your KPI scores or company results. The pool is decided and committed when you join — it will be paid in full if you stay for 5 years, regardless of performance ratings. Think of it as the Company&#8217;s commitment to you that grows more valuable the longer you stay.</p>
    <p style="margin:0 0 6px 0;"><strong>This IS cash:</strong> The Retention Bonus is a <strong>direct cash payment</strong> – not stock options, not deferred equity. You will see each chunk credited to your salary account at end of Year 1, Year 2, Year 3, Year 4, and Year 5. Each payment is taxed as salary income (u/s 15 of IT Act, 1961), and TDS is deducted by the employer at source.</p>
    <p style="margin:0 0 6px 0;"><strong>Why 5 chunks?</strong> The 5-chunk structure ensures meaningful long-term retention. An employee who completes all 5 years receives the <strong>full pool amount</strong>; an employee who leaves early receives only the chunks already paid — unreleased chunks are forfeited.</p>
    <p style="margin:0 0 6px 0;"><strong>Tax treatment:</strong> Every tranche is treated as salary income in the year of receipt, taxed u/s 15 of the Income Tax Act, 1961. TDS is deducted in that month’s payroll at the applicable slab rate. There is no deferred tax or capital gains treatment – this is straightforward cash compensation.</p>
    <p style="margin:0 0 6px 0;"><strong>Formal terms:</strong> {{S_ESOP_NOTE}}</p>
    <p style="margin:0;"><strong>Your 5-chunk payout schedule (5 equal cash chunks, 20% of pool each):</strong></p>
  </div>
  {{S_ESOP_TABLE}}

  <div style="border:2px solid #dc2626;border-radius:8px;padding:14px 18px;background:#fff5f5;margin:14px 0;">
    <h4 style="color:#dc2626;margin:0 0 10px 0;font-size:11pt;">&#9888;&nbsp; Important: Retention Bonus – Forfeiture &amp; Early Exit Terms</h4>
    <p style="margin:0 0 8px 0;font-size:10.5pt;color:#374151;">The Retention Bonus is a <strong>committed pool with cliff-release per chunk</strong>. The following terms govern your entitlement upon any form of separation from the Company, and form a binding part of this employment agreement:</p>
    <ol style="margin:6px 0;padding-left:20px;font-size:10.5pt;line-height:1.9;color:#1f2937;">
      <li><strong>Voluntary Resignation Before Chunk Release Date:</strong> If you voluntarily resign or initiate separation before the last working day of any given service year, the chunk for that year and <strong>all subsequent unreleased chunks are forfeited in full</strong>, with no pro-rated or partial payment under any circumstances.</li>
      <li><strong>Termination for Cause:</strong> If the Company terminates your employment for cause — including but not limited to gross misconduct, fraud, theft, breach of confidentiality, insubordination, or violation of Company policy — all unreleased chunks are <strong>immediately and irrevocably forfeited</strong>. No compensation or damages shall be payable for forfeited chunks.</li>
      <li><strong>No Pro-Rata Accrual:</strong> The Retention Bonus does not accrue on a monthly or daily basis. Entitlement to each chunk arises <em>only</em> upon completing the full year of service associated with that chunk, while actively employed on the last working day of that year. Partial year service confers zero entitlement to the chunk for that year.</li>
      <li><strong>Paid Chunks Are Non-Recoverable:</strong> Chunks already disbursed and credited to your bank account before your last working date are fully earned and non-recoverable by the Company, except where subject to other express recovery clauses in this agreement (e.g., bond breach under Clause 6.3).</li>
      <li><strong>Mutual Separation:</strong> In cases of mutually agreed separation, any disbursement of unreleased chunks shall be entirely at the Company&#8217;s sole discretion and must be explicitly confirmed in a written separation agreement signed by both parties. No implied or verbal communication shall constitute such agreement.</li>
      <li><strong>Death or Permanent Incapacitation:</strong> In the event of the employee&#8217;s death or a medically certified permanent disability rendering continued employment impossible, the Company may, at its sole discretion and upon appropriate documentation and board-level approval, release unreleased chunks to the employee&#8217;s legal nominee or heir. This shall not be construed as a general entitlement.</li>
      <li><strong>Acknowledgement:</strong> By signing this offer letter, you explicitly acknowledge that you have read, understood, and irrevocably accepted these forfeiture terms as a material condition of receiving the Retention Bonus. You waive any right to claim unreleased chunks upon separation for any reason not expressly protected herein.</li>
    </ol>
    <p style="margin:8px 0 0 0;font-size:10pt;color:#7f1d1d;border-top:1px dashed #fca5a5;padding-top:8px;"><em>These terms are governed by and construed in accordance with the laws of India. Any dispute shall be subject to the exclusive jurisdiction of courts in Lucknow, Uttar Pradesh.</em></p>
  </div>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h3 style="color:#2c5282;margin-top:12px;">3.7 Variable Pay – Detailed Terms &amp; Conditions</h3>
  <p style="color:#374151;font-size:10.5pt;">This section sets out the formal and plain-language terms for your Variable Pay. Variable Pay is a <strong>performance-linked annual bonus</strong> whose maximum pool is committed in your CTC from day one. Unlike the Retention Bonus (which is not KPI-linked), the actual cash released each year is determined by your Performance KPI score as set by HR/management after the annual review.</p>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <tr><td style="${TDLBLC}">Variable Pay Pool (Maximum)</td><td style="${TDC}">{{S_VARIABLE_POOL_CELL}}</td></tr>
    <tr><td style="${TDLBL}">Release Formula</td><td style="${TD}">Actual Release = Variable Pay Pool &times; Performance KPI% (e.g., if pool is &#x20B9;50,000 and KPI = 70%, actual release = &#x20B9;35,000)</td></tr>
    <tr><td style="${TDLBLC}">Performance KPI</td><td style="${TDC}">Set by HR / management on an annual basis (0%–100%). Default KPI at joining = 0%. Communicated to employee before disbursement.</td></tr>
    <tr><td style="${TDLBL}">Payment Frequency</td><td style="${TD}"><strong>Once a year</strong> — at end of financial year (April, after 31 March close). <strong>Not paid monthly.</strong> Not reflected in regular monthly payslips.</td></tr>
    <tr><td style="${TDLBLC}">Eligibility Condition</td><td style="${TDC}">Employee must be on active rolls and employed with the Company on 31 March of that financial year to be eligible for release. No pro-rata accrual for resignations mid-year.</td></tr>
    <tr><td style="${TDLBL}">Taxability</td><td style="${TD}">Fully taxable as salary income u/s 15 of the Income Tax Act, 1961. Classified as performance bonus under the salary head. TDS deducted by the Company in the month of release.</td></tr>
    <tr><td style="${TDLBLC}">CTC Declaration</td><td style="${TDC}">CTC declares the <strong>full maximum pool</strong>. Actual disbursement in any given year may be less (including zero) depending on Performance KPI. No legal right accrues to the full pool amount unless KPI = 100%.</td></tr>
    <tr><td style="${TDLBL}">Applicability Band</td><td style="${TD}">{{S_VARIABLE_APPLIC_CELL}}</td></tr>
  </table>
  <div style="border-left:4px solid #f59e0b;background:#fffbeb;padding:10px 14px;margin:8px 0;font-size:10.5pt;color:#78350f;">
    <strong>&#9888;&nbsp; Important:</strong> Variable Pay is a <em>performance-linked incentive</em>, not a guaranteed component of your monthly salary. The Company commits to maintaining the pool in your CTC; however, actual disbursement is contingent on Performance KPI scores set annually by management. Employees joining mid-year will have their first year's Variable Pay reviewed on a pro-rata basis at management's discretion.
  </div>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h3 style="color:#2c5282;margin-top:12px;">3.8 Health Insurance &amp; Other Benefits</h3>
  <p>As of the date of this offer, the Company does not provide group health insurance / mediclaim coverage as a standard benefit. This is not included in, and does not form part of, your CTC. Should the Company introduce such a benefit in the future (for all or specific categories of employees), it will be communicated to you separately in writing, and any associated cost implications for the Company shall not automatically alter your agreed CTC unless expressly stated at that time.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">4. ONE-TIME JOINING / RELOCATION BONUS</h2>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <tr><td style="${TDLBLC}">Joining / Relocation Bonus (One-Time)</td><td style="${TDC}">{{JOINING_BONUS_AMT}}</td></tr>
    <tr><td style="${TDLBL}">Payment Timeline</td><td style="${TD}">Disbursed within 30 (Thirty) days of confirmed joining, upon submission of requisite relocation documents or at the Company's discretion.</td></tr>
    <tr><td style="${TDLBLC}">Taxability</td><td style="${TDC}">This amount is fully taxable as income in the hands of the employee</td></tr>
    <tr><td style="${TDLBL}">Recovery Clause</td><td style="${TD}">If <strong>you voluntarily resign or leave</strong> within 12 (twelve) months of the date of joining, the Company reserves the right to recover the full relocation bonus amount. This recovery clause does not apply in the event of Company-initiated separation without cause.</td></tr>
  </table>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;">
    The Joining / Relocation Bonus of {{JOINING_BONUS_AMT}} is a <strong>one-time, non-recurring</strong> payment forming part of the total CTC of {{CTC_AMOUNT}} for Year 1. It is payable separately from the monthly salary and does not form part of the recurring monthly pay structure. From Year 2 onwards, the monthly gross salary shall be {{MONTHLY_GROSS}}/mo ({{ANNUAL_RECURRING}}/yr), plus applicable Retention Bonus Chunks 2–5 as described in Section 3.6 above.
  </blockquote>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">5. EMPLOYMENT TYPE &amp; PROBATION PERIOD</h2>
  <h3 style="color:#2c5282;margin-top:12px;">5.1 Nature of Employment</h3>
  <p>Your employment with PrrepX Pathshala Private Limited is <strong>full-time and permanent</strong>, subject to the terms and conditions of this offer letter, the Company's HR policies, the Employees' Service Rules, and any other applicable agreements in force from time to time.</p>
  <h3 style="color:#2c5282;margin-top:12px;">5.2 Probation Period</h3>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <tr><td style="${TDLBLC}">Probation Duration</td><td style="${TDC}">{{PROBATION_PERIOD}} from the Date of Joining</td></tr>
    <tr><td style="${TDLBL}">Confirmation</td><td style="${TD}">Upon successful completion per performance evaluation</td></tr>
    <tr><td style="${TDLBLC}">Extension Rights</td><td style="${TDC}">The Company may extend probation by up to 3 (Three) additional months</td></tr>
  </table>
  <p>During the probation period:</p>
  <ul>
    <li>Your performance, conduct, attitude, attendance, and adherence to Company policies will be closely monitored.</li>
    <li>Either party may terminate employment during probation by giving <strong>7 (Seven) calendar days'</strong> written notice or payment in lieu thereof — or such longer notice as may be required under the applicable state Shops &amp; Commercial Establishments Act based on the length of service completed at that point.</li>
    <li>At the end of the probation period, your employment will be confirmed in writing if your performance is found satisfactory. <strong>No confirmation shall be implied by silence or continued employment unless a formal written confirmation letter is issued.</strong></li>
    <li>The Company reserves the right to terminate employment at the end of the probation period without assigning any reason, if performance is deemed unsatisfactory, by serving the applicable notice.</li>
    <li><strong>No Leave During Probation:</strong> No leave of any kind shall be granted during the Probation Period — this includes Earned Leave (EL), Casual Leave (CL), Sick Leave (SL), and Unpaid Leave (UL). Employees do not have the right to apply for any leave — paid or unpaid — during probation. Any absence must be prior-approved by admin/HR and will be recorded as Leave Without Pay (LWP) at the Company&apos;s discretion. This restriction does not affect statutory Maternity Leave entitlements under the Maternity Benefit (Amendment) Act, 2017, which remain in force regardless of probation status. Leave entitlements begin to accrue only from the date of formal written confirmation of employment.</li>
  </ul>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">6. SERVICE AGREEMENT (EMPLOYMENT BOND)</h2>
  <h3 style="color:#2c5282;margin-top:12px;">6.1 Mandatory Service Period</h3>
  <p>In consideration of the employment, compensation, training, knowledge, resources, client exposure, and business opportunities provided by the Company, you agree and commit to serve PrrepX Pathshala Private Limited for a <strong>minimum continuous period of 2 (Two) years</strong> ("Bond Period") commencing from your <strong>Date of Joining / Date of Confirmation</strong> (whichever is applicable as mutually agreed).</p>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;">
    <strong>Bond Commencement Date:</strong> Date of Joining<br/>
    <strong>Bond End Date:</strong> 2 (Two) years from Bond Commencement Date
  </blockquote>
  <h3 style="color:#2c5282;margin-top:12px;">6.2 Rationale &amp; Justification</h3>
  <p>The Company invests substantially in training, onboarding, content access, tools, mentorship, academic exposure, and building competencies in each employee. The Company's internal knowledge base, proprietary content, student data, academic methodologies, and business strategies represent significant intellectual and financial investment. The Service Agreement is designed to protect this investment and ensure stability of operations, and is in accordance with principles upheld by Indian courts where such agreements are reasonable and supported by adequate consideration.</p>
  <h3 style="color:#2c5282;margin-top:12px;">6.3 Bond Breach (Training &amp; Content Indemnity)</h3>
  <p><strong>If you voluntarily resign or abandon your employment before completing the Bond Period of 2 (Two) years</strong>, you shall be liable to pay the Company the full Bond Breach compensation as set out below. The Company expressly reserves its rights to pursue all available legal remedies for recovery, including civil and/or criminal proceedings where applicable.</p>
  <blockquote style="border-left:4px solid #c0392b;padding:8px 16px;background:#fff5f5;margin:12px 0;color:#c0392b;">
    <strong>Bond Breach Compensation:</strong> Payable = Full Bond Breach Amount as defined below (no percentage reductions or pro‑rata scaling shall apply).<br/>
    <strong>Bond Breach Amount = 3 (Three) months' Gross Monthly Salary</strong> (as defined in Section 3.2 — fixed recurring Basic + HRA + LTA + Medical + Telephone + Special).<br/>
    <em>Joining Bonus recovery, where applicable, is governed separately by Clause 4/6.5 and shall be payable in full as a separate obligation.</em>
  </blockquote>
  <p style="font-size:10.5pt;color:#374151;"><em>For the avoidance of doubt: the Company will not accept any pro‑rata reductions, percentage formulas, or examples that reduce the Bond Breach Amount. The full Bond Breach Amount is payable on breach occurring at any time before the completion of the Bond Period (two years).</em></p>
  <p><strong>The Bond Breach obligation is a genuine pre‑estimate of loss and the Company reserves its right to recover the full amount through any lawful means, including but not limited to deduction from final settlements, recovery from security deposits, civil proceedings, arbitration, and any other remedies available under law.</strong></p>
  <h3 style="color:#2c5282;margin-top:12px;">6.4 Mode of Recovery</h3>
  <p>The bond compensation shall be recoverable by the Company through any or all of the following means:</p>
  <ul>
    <li>Deduction from Full and Final Settlement (F&amp;F) amount, including any pending salary, incentives, LTA, or bonus</li>
    <li>Recovery from the Security Deposit (if any)</li>
    <li>Legal proceedings under applicable Indian law, including but not limited to the Indian Contract Act, 1872, and the Specific Relief Act, 1963</li>
    <li>Recovery through arbitration as per the Arbitration &amp; Conciliation Act, 1996</li>
  </ul>
  <h3 style="color:#2c5282;margin-top:12px;">6.5 Exit During Probation (Special Provision – First Month)</h3>
  <p>If you voluntarily resign or fail to join within <strong>the first 30 (Thirty) calendar days</strong> from the date of joining, you shall be liable to:</p>
  <ul>
    <li>Return the <strong>full Relocation / Joining Bonus</strong> of {{JOINING_BONUS_AMT}} to the Company</li>
    <li>Pay the full Bond Breach Amount as specified in Clause 6.3 above (the Company will not apply pro‑rata reductions in the first month)</li>
  </ul>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;font-style:italic;">
    Early exits during the first month are particularly disruptive as they consume significant onboarding and orientation resources. The Company requests that all candidates accept this offer only upon genuine commitment to the minimum bond period.
  </blockquote>
  <h3 style="color:#2c5282;margin-top:12px;">6.6 Bond Waiver</h3>
  <p>The Company, at its sole discretion, may waive or reduce the bond compensation in exceptional circumstances such as:</p>
  <ul>
    <li>Documented critical personal medical emergency</li>
    <li>Passing of an immediate family member with permanent caregiving obligations</li>
    <li>Any other compassionate ground reviewed and approved in writing by the Founder/Managing Director</li>
  </ul>
  <p>Such waiver shall not constitute a precedent or be cited in future cases.</p>
  <h3 style="color:#2c5282;margin-top:12px;">6.7 Obligation Survival</h3>
  <p>The obligations under this Service Agreement shall survive the termination or expiry of employment to the extent necessary to give effect to the intent of this clause.</p>

  <h3 style="color:#2c5282;margin-top:14px;">6.8 Pre-Joining Breach</h3>
  <p>Once you have signed and returned this Offer Letter to the Company — whether physically, by scanned copy, or electronically — your acceptance is <strong>legally binding and irrevocable</strong> under the Indian Contract Act, 1872. The following acts constitute a <strong>Pre-Joining Breach</strong>:</p>
  <ul>
    <li><strong>Withdrawal of Acceptance:</strong> Attempting to withdraw, cancel, or revoke your acceptance of this offer after it has been communicated to the Company, for any reason.</li>
    <li><strong>Unilateral Postponement of Joining Date:</strong> Requesting to delay or reschedule the agreed Date of Joining without the Company&apos;s prior written consent.</li>
    <li><strong>Failure to Join:</strong> Not reporting for duty on the agreed Date of Joining without prior written approval from the Company.</li>
  </ul>
  <p>As no training, content access, or on-the-job investment has yet been made in a candidate who has not joined, a Pre-Joining Breach does <strong>not</strong> attract the Bond Breach (Training &amp; Content Indemnity) under Clause 6.3. Instead, in the event of a Pre-Joining Breach, the Company may recover its <strong>genuine pre-estimate of actual costs incurred</strong> up to that point (e.g., background verification already initiated, seat/resource reservation, and administrative/recruitment costs of re-advertising the role), subject to a maximum of <strong>₹15,000/- (Rupees Fifteen Thousand Only)</strong>, as liquidated damages under Section 74 of the Indian Contract Act, 1872. This amount is independent of, and shall not be combined with, any Bond Breach compensation under Clause 6.3 (which applies only after joining).</p>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;font-style:italic;">
    Note: The Company reserves the right to revise the Date of Joining for its own operational reasons. Such Company-initiated rescheduling does not create any financial obligation for the employee. Only a unilateral delay, withdrawal, or failure by the employee triggers Pre-Joining Breach liability. Candidates are strongly advised to accept this offer only upon genuine, unconditional commitment to join on the stated date.
  </blockquote>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">7. WORKING HOURS &amp; ATTENDANCE</h2>
  <h3 style="color:#2c5282;margin-top:12px;">7.1 Standard Working Hours</h3>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <tr><td style="${TDLBLC}">Working Days</td><td style="${TDC}">6 days per week — Monday to Saturday</td></tr>
    <tr><td style="${TDLBL}">Daily Working Hours</td><td style="${TD}">As per applicable state Shops &amp; Establishments Act — typically 8 productive hours per day</td></tr>
    <tr><td style="${TDLBLC}">Weekly Hours</td><td style="${TDC}">Not to exceed 48 hours per week in compliance with applicable labor law</td></tr>
    <tr><td style="${TDLBL}">Office Timing</td><td style="${TD}">9:30 AM to 6:30 PM (with a 60-minute break)</td></tr>
  </table>
  <h3 style="color:#2c5282;margin-top:12px;">7.2 Flexibility and Adjustments</h3>
  <p>The Company reserves the right to modify working hours, weekly-off schedules, and shift timings based on business requirements and applicable law. Changes will be communicated with reasonable advance notice. Employees may be required to attend special sessions, events, launches, or online classes outside standard hours, for which compensatory adjustments shall be offered where feasible.</p>
  <h3 style="color:#2c5282;margin-top:12px;">7.3 Attendance</h3>
  <ul>
    <li>You are expected to maintain regular and punctual attendance.</li>
    <li>Attendance records will be maintained digitally. Uninformed absences may result in salary deductions and disciplinary action.</li>
    <li>In case of inability to report to work, you must inform your reporting manager prior to or by 10:00 AM of the concerned day.</li>
  </ul>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">8. LEAVE POLICY</h2>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <thead style="background:#1a3c6e;color:#fff;">
      <tr>
        <th style="${TH};width:38%;">Leave Type</th>
        <th style="${TH};width:22%;">Entitlement</th>
        <th style="${TH}">Governing Rule</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="${TDC}"><strong>Earned Leave (EL) / Privilege Leave (PL)</strong></td>
        <td style="${TDC}">12–15 days per annum</td>
        <td style="${TDC}">Accrued monthly; per state Shops &amp; Establishments Act</td>
      </tr>
      <tr>
        <td style="${TD}"><strong>Casual Leave (CL)</strong></td>
        <td style="${TD}">6–8 days per annum</td>
        <td style="${TD}">Non-encashable; non-carry-forward</td>
      </tr>
      <tr>
        <td style="${TDC}"><strong>Sick Leave (SL)</strong></td>
        <td style="${TDC}">6–8 days per annum</td>
        <td style="${TDC}">Subject to medical certificate for &gt;2 consecutive days</td>
      </tr>
      <tr>
        <td style="${TD}"><strong>Maternity Leave</strong></td>
        <td style="${TD}">As per Maternity Benefit (Amendment) Act, 2017</td>
        <td style="${TD}">26 weeks paid for 1st/2nd child</td>
      </tr>
      <tr>
        <td style="${TDC}"><strong>Paternity Leave</strong></td>
        <td style="${TDC}">5 (Five) days (Company Policy)</td>
        <td style="${TDC}">Subject to HR approval</td>
      </tr>
      <tr>
        <td style="${TD}"><strong>Bereavement Leave</strong></td>
        <td style="${TD}">3 (Three) days per event</td>
        <td style="${TD}">Immediate family only</td>
      </tr>
      <tr>
        <td style="${TDC}"><strong>National Holidays</strong></td>
        <td style="${TDC}">As per Company holiday calendar</td>
        <td style="${TDC}">Released at start of each year; see mandatory note below</td>
      </tr>
    </tbody>
  </table>
  <p>Leave entitlements under this offer are subject to the state-specific Shops and Commercial Establishments Act applicable to your place of work. National and public holidays will be as per the official Company Holiday Calendar published each year.</p>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;">
    <strong>Mandatory National Holidays:</strong> The three National Holidays — Republic Day (26 January), Independence Day (15 August), and Gandhi Jayanti (2 October) — are treated by the Company as mandatory paid rest days in line with prevailing national practice and the applicable State Shops &amp; Commercial Establishments Act. If work is required on any of these three days, the employee shall be entitled to a compensatory off day or additional pay as applicable under law. All other public and gazetted holidays are at the Company&apos;s discretion and will be communicated in advance.
  </blockquote>
  <blockquote style="border-left:4px solid #c0392b;padding:8px 16px;background:#fff5f5;margin:12px 0;">
    <strong>Leave during Probation:</strong> No leave of any kind is available during the entire Probation Period of {{PROBATION_PERIOD}} from the Date of Joining — this explicitly includes Earned Leave (EL), Casual Leave (CL), Sick Leave (SL), and Unpaid Leave (UL). Employees cannot self-apply for any leave during probation, whether paid or unpaid. Any absence requires prior admin/HR approval and will be recorded as Leave Without Pay (LWP) at the Company&apos;s discretion. Statutory Maternity Leave rights under the Maternity Benefit (Amendment) Act, 2017 are an exception and cannot be denied. Leave entitlements begin accruing only after formal written confirmation of employment is issued.
  </blockquote>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">9. CODE OF CONDUCT, DISCIPLINE &amp; CONSEQUENCES</h2>

  <h3 style="color:#2c5282;margin-top:12px;">9.1 Expected Standards of Conduct</h3>
  <p>You are expected at all times to conduct yourself in a professional, ethical, and lawful manner, consistent with the values, mission, and reputation of PrrepX Pathshala. This includes but is not limited to:</p>
  <p><strong>(a) Professionalism:</strong> Timely delivery of responsibilities, respectful communication with colleagues, students, parents, and vendors; adherence to hierarchy, reporting lines, and institutional processes.</p>
  <p><strong>(b) Digital &amp; Social Media Conduct:</strong> Professional behaviour on all digital platforms, including email, WhatsApp/Teams/Slack, social media, internal tools, and video conferencing. You must not make any public statements, posts, or comments that could harm the Company's brand, reveal confidential information, or disparage colleagues, management, students, or the institution.</p>
  <p><strong>(c) Student &amp; Parental Interaction:</strong> Given the educational and child-centric nature of our business, all interactions with students, parents, and academic stakeholders must be appropriate, respectful, ethical, and aligned with the Company's Safe Conduct &amp; Child Protection Policy at all times — whether in person, online, or over the phone.</p>
  <p><strong>(d) Substance &amp; Conduct Prohibition:</strong> Consumption of alcohol, narcotics, or any controlled substance, or the use of abusive, threatening, or offensive language on Company premises, during Company activities, or while representing the Company in any capacity, is <strong>strictly prohibited</strong>.</p>
  <p><strong>(e) Workplace Respect &amp; Zero-Tolerance Policy:</strong> Any form of harassment — sexual, verbal, written, physical, or cyber — directed at any colleague, student, parent, vendor, or any other person connected with the Company's operations is <strong>absolutely prohibited</strong> and constitutes a ground for immediate dismissal.</p>

  <h3 style="color:#2c5282;margin-top:14px;">9.2 Acts of Misconduct &amp; Policy Violations</h3>
  <p>The following constitute acts of misconduct (whether minor or gross) and shall subject the employee to disciplinary proceedings:</p>
  <table style="width:100%;border-collapse:collapse;font-size:10.5pt;margin-bottom:12px;">
    <thead style="background:#1a3c6e;color:#fff;">
      <tr><th style="padding:8px 10px;text-align:left;width:28%;">Category</th><th style="padding:8px 10px;text-align:left;">Examples of Violations</th></tr>
    </thead>
    <tbody>
      <tr style="background:#f8faff;"><td style="padding:8px 10px;font-weight:600;border-bottom:1px solid #e2e8f0;">Attendance &amp; Duties</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;">Habitual late attendance, unauthorised absenteeism, abandonment of duty, failure to submit reports or deliverables without valid reason</td></tr>
      <tr><td style="padding:8px 10px;font-weight:600;border-bottom:1px solid #e2e8f0;">Insubordination</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;">Wilful refusal to follow lawful and reasonable instructions from superiors; disrespectful or defiant behaviour toward management</td></tr>
      <tr style="background:#f8faff;"><td style="padding:8px 10px;font-weight:600;border-bottom:1px solid #e2e8f0;">Dishonesty &amp; Fraud</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;">Falsification of records, expense claims, attendance data, academic credentials, or any Company documents; misappropriation of Company funds or assets</td></tr>
      <tr><td style="padding:8px 10px;font-weight:600;border-bottom:1px solid #e2e8f0;">Confidentiality Breach</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;">Unauthorised disclosure of student data, question papers, business strategies, pricing, or any confidential information to outside parties</td></tr>
      <tr style="background:#f8faff;"><td style="padding:8px 10px;font-weight:600;border-bottom:1px solid #e2e8f0;">Harassment &amp; Bullying</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;">Any form of workplace harassment, bullying, intimidation, discrimination, or hostile behaviour toward any person</td></tr>
      <tr><td style="padding:8px 10px;font-weight:600;border-bottom:1px solid #e2e8f0;">Damage to Property</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;">Wilful damage, theft, or misuse of Company property, assets, software, or digital systems</td></tr>
      <tr style="background:#f8faff;"><td style="padding:8px 10px;font-weight:600;border-bottom:1px solid #e2e8f0;">Conflict of Interest</td><td style="padding:8px 10px;border-bottom:1px solid #e2e8f0;">Working for a competing organisation, soliciting Company clients or students for personal gain, or taking up undisclosed paid employment during the employment period</td></tr>
      <tr><td style="padding:8px 10px;font-weight:600;">Reputational Harm</td><td style="padding:8px 10px;">Making false, defamatory, or inflammatory statements about the Company, its management, colleagues, or students, publicly or privately</td></tr>
    </tbody>
  </table>

  <h3 style="color:#2c5282;margin-top:14px;">9.3 Disciplinary Process</h3>
  <p>The Company shall follow a graduated disciplinary process proportionate to the nature and severity of the misconduct:</p>
  <ol style="font-size:10.5pt;line-height:1.9;">
    <li><strong>Verbal Warning:</strong> For minor or first-time infractions, a verbal counselling/warning shall be issued by the reporting manager and documented internally.</li>
    <li><strong>Written Warning (Show-Cause Notice):</strong> For repeated minor misconduct or a moderate violation, a written Show-Cause Notice (SCN) shall be issued. The employee must respond in writing within <strong>48 hours</strong> (or as specified). Failure to respond shall be treated as admission of the alleged conduct.</li>
    <li><strong>Suspension Pending Enquiry:</strong> The Company reserves the right to place the employee on <strong>suspension pending a formal inquiry</strong>, where the conduct is of a serious nature or where the employee's continued presence may compromise the inquiry or the safety of others. During such suspension, the employee shall be paid a subsistence allowance of 50% of gross salary for the first 90 days, and 75% thereafter if the inquiry extends beyond 90 days, in line with the subsistence-allowance principle under the Industrial Employment (Standing Orders) Act, 1946.</li>
    <li><strong>Formal Disciplinary Enquiry:</strong> A designated Enquiry Officer may be appointed to conduct a fair and impartial inquiry. The employee shall be given a reasonable opportunity to present their case, call witnesses, and submit evidence.</li>
    <li><strong>Disciplinary Outcome:</strong> Upon conclusion of the inquiry, the Company may impose one or more of the following: (a) Written Warning on record; (b) Withholding of increment or bonus; (c) Demotion or change of role; (d) Recovery of losses caused; (e) Termination of employment, with or without notice, depending on severity.</li>
  </ol>
  <blockquote style="border-left:4px solid #dc2626;padding:8px 16px;background:#fff5f5;margin:10px 0;font-size:10.5pt;">
    <strong>Gross Misconduct — Summary Dismissal:</strong> In cases of fraud, sexual harassment, violence, serious breach of confidentiality, theft, or any act that brings the Company into disrepute, the Company reserves the right to <strong>summarily dismiss the employee without notice, without prior inquiry, and without any payment in lieu of notice</strong>, subject to compliance with applicable labour laws.
  </blockquote>

  <h3 style="color:#2c5282;margin-top:14px;">9.4 Company's Rights in Disciplinary Matters</h3>
  <p>The Company expressly reserves the following rights in connection with any disciplinary matter:</p>
  <ul style="font-size:10.5pt;line-height:1.9;">
    <li>To <strong>investigate</strong> any alleged misconduct through internal or external means, including review of digital records, emails, device usage logs, and CCTV footage.</li>
    <li>To <strong>withhold salary or dues</strong> during a period of unpaid suspension, or to adjust proven financial losses against any dues payable to the employee, as permitted by applicable law.</li>
    <li>To <strong>pursue civil or criminal proceedings</strong> in addition to internal disciplinary action, where the misconduct constitutes a cognisable offence under applicable Indian law (IPC/BNS or otherwise).</li>
    <li>To <strong>recover financial losses</strong> caused by misconduct, negligence, or breach of duty from any dues payable to the employee, including Full &amp; Final settlement amounts.</li>
    <li>To <strong>communicate findings</strong> to regulatory, statutory, or law enforcement authorities where legally required or where the Company's interests necessitate such disclosure.</li>
    <li>To <strong>blacklist or not provide a reference</strong> for an employee dismissed for gross misconduct, where permitted and in accordance with applicable legal obligations.</li>
  </ul>

  <h3 style="color:#2c5282;margin-top:14px;">9.5 Non-Retaliation &amp; Whistleblower Protection</h3>
  <p>Any employee who raises a genuine complaint of misconduct, harassment, or policy violation in good faith shall be protected against retaliation, victimisation, or adverse employment action. Retaliatory conduct by any employee against a complainant shall itself constitute a disciplinary offence and may result in termination.</p>

  <h3 style="color:#2c5282;margin-top:14px;">9.6 POSH Compliance (Prevention of Sexual Harassment)</h3>
  <p>The Company is committed to a safe, respectful, and harassment-free workplace. In accordance with the <strong>Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013</strong>, an Internal Complaints Committee (ICC) has been constituted. All employees are required to complete mandatory POSH awareness training as directed by HR. Violations of POSH norms constitute gross misconduct and may result in summary dismissal and/or criminal prosecution under applicable law.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">10. CONFIDENTIALITY &amp; NON-DISCLOSURE</h2>
  <h3 style="color:#2c5282;margin-top:12px;">10.1 Confidential Information</h3>
  <p>You acknowledge and agree that during the course of your employment, you will have access to, and will be entrusted with, highly sensitive and proprietary information of the Company ("Confidential Information"), including but not limited to:</p>
  <ul>
    <li>Academic content, question banks, test papers, answer keys</li>
    <li>Student databases, personal data, academic records, contact information</li>
    <li>Business strategies, pricing, revenue models, growth plans</li>
    <li>Technology systems, source code, product roadmaps</li>
    <li>Vendor, partner, and supplier agreements</li>
    <li>Employee data and internal HR policies</li>
    <li>Marketing campaigns, growth strategies, product pipelines</li>
  </ul>
  <h3 style="color:#2c5282;margin-top:12px;">10.2 Obligations</h3>
  <p>You shall:</p>
  <ul>
    <li>Maintain strict confidentiality of all Confidential Information during and after the term of your employment</li>
    <li>Not disclose, share, transmit, publish, or otherwise reveal any Confidential Information to any third party, including former employers, competitors, or educational institutions, without prior written consent of the Company</li>
    <li>Take reasonable care to prevent unauthorized access to Confidential Information</li>
    <li>Upon separation, immediately return all physical and digital documents, devices, and data belonging to the Company</li>
  </ul>
  <h3 style="color:#2c5282;margin-top:12px;">10.3 Survival</h3>
  <p>This obligation of confidentiality shall survive the termination of employment and remain binding for a period of <strong>3 (Three) years</strong> post-separation.</p>
  <h3 style="color:#2c5282;margin-top:12px;">10.4 Breach</h3>
  <p>Any unauthorized disclosure of Confidential Information shall constitute a material breach of this contract and may attract civil and/or criminal liability under:</p>
  <ul>
    <li>The Indian Contract Act, 1872</li>
    <li>The Information Technology Act, 2000 (and its amendments)</li>
    <li>The Digital Personal Data Protection Act, 2023 (DPDP Act)</li>
    <li>Such other laws as may be applicable</li>
  </ul>

  <h3 style="color:#2c5282;margin-top:12px;">10.5 Notice on Collection of Your Personal Data (DPDP Act, 2023)</h3>
  <p style="font-size:10.5pt;">In accordance with Section 5 of the Digital Personal Data Protection Act, 2023, this Notice informs you of the following before you provide your personal data to the Company:</p>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <tr><td style="${TDLBLC}">Personal Data Collected</td><td style="${TDC}">Name, contact details, address, PAN, Aadhaar, bank account details, educational and employment history, photographs, emergency contact details, and other data required for employment, payroll, and statutory compliance</td></tr>
    <tr><td style="${TDLBL}">Purpose of Processing</td><td style="${TD}">Employment administration, payroll and statutory deductions (TDS, PF, ESIC, Professional Tax), background verification, statutory record-keeping, internal HR systems, and workplace monitoring (e.g., attendance, email, CCTV, device logs) as described in Clause 9.4</td></tr>
    <tr><td style="${TDLBLC}">Consent &amp; Withdrawal</td><td style="${TDC}">By accepting this offer and providing your personal data, you consent to such processing for the stated purposes. You may withdraw consent for non-mandatory processing at any time by written request to HR, subject to the Company's ability to continue statutorily mandated processing (e.g., payroll, tax records) regardless of withdrawal</td></tr>
    <tr><td style="${TDLBL}">Grievance / Data Protection Officer</td><td style="${TD}">Any grievance regarding processing of your personal data may be raised in writing to HR at the Company's registered office address stated in this letter</td></tr>
    <tr><td style="${TDLBLC}">Retention</td><td style="${TDC}">Personal data shall be retained for the duration of employment and thereafter for the period required under applicable statutory record-keeping obligations, after which it shall be erased in accordance with the Company's data retention policy</td></tr>
  </table>
  <p style="font-size:10pt;color:#4a5568;"><em>This Notice is provided in addition to, and does not replace, the Company's detailed Privacy Policy which may be updated from time to time and shared separately.</em></p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">11. INTELLECTUAL PROPERTY RIGHTS</h2>
  <h3 style="color:#2c5282;margin-top:12px;">11.1 Assignment of Intellectual Property</h3>
  <p>All work product, deliverables, outputs, discoveries, innovations, and intellectual property created, conceived, developed, or reduced to practice by you <strong>during the course of your employment</strong>, whether individually or collectively, shall be the <strong>exclusive and sole intellectual property of PrrepX Pathshala Private Limited.</strong></p>
  <p>This includes, without limitation:</p>
  <ul>
    <li>Teaching content, lecture recordings, video/audio materials, notes, and study modules</li>
    <li>Question sets, mock tests, practice papers, and answer keys</li>
    <li>Course curricula, learning pathways, and educational methodologies</li>
    <li>Software, code, applications, scripts, bots, or digital tools</li>
    <li>Graphics, illustrations, branding assets, and design files</li>
    <li>Social media content, marketing collateral, and campaign materials</li>
    <li>Research and analysis reports</li>
  </ul>
  <h3 style="color:#2c5282;margin-top:12px;">11.2 No Personal Use or External Publication</h3>
  <p>You shall not:</p>
  <ul>
    <li>Reproduce, sell, license, or otherwise commercially exploit any Company IP outside of your role</li>
    <li>Publish, upload, or distribute Company content on personal platforms, YouTube channels, competing portals, social media, or third-party apps</li>
    <li>Use student data or Company databases for personal gain or external business</li>
  </ul>
  <h3 style="color:#2c5282;margin-top:12px;">11.3 Post-Employment</h3>
  <p>All IP created during your employment remains the property of the Company even after separation, and you shall execute any additional assignment documents as the Company may reasonably request.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">12. NON-COMPETE, EXCLUSIVE SERVICE &amp; NON-SOLICITATION</h2>
  <h3 style="color:#2c5282;margin-top:12px;">12.1 Non-Compete &amp; General Exclusivity (During Employment)</h3>
  <p>During the term of your employment, you shall not, directly or indirectly:</p>
  <ul>
    <li>Engage in, establish, advise, join, or work for any business that is in direct or indirect competition with PrrepX Pathshala&apos;s business.</li>
    <li>Take up any secondary, freelance, part-time, or contractual employment without prior express written approval from the Company.</li>
  </ul>
  <h3 style="color:#2c5282;margin-top:14px;">12.1A Exclusive Service &mdash; Prohibition on Outside Teaching &amp; Educational Content</h3>
  <p>PrrepX Pathshala is an educational content company. Your role directly involves the creation and delivery of academic content. Accordingly, the following specific restrictions apply <strong>during the entire term of your employment</strong>, regardless of whether the activity is paid or unpaid:</p>
  <ul>
    <li><strong>No External Institute Teaching:</strong> You shall not teach, lecture, or deliver sessions at any other coaching institute, school, college, or educational institution &mdash; in any format (regular classes, guest lectures, demo sessions, workshops, or otherwise).</li>
    <li><strong>No Private or Group Tuitions:</strong> You shall not provide private or group tuitions to students &mdash; whether at your premises, a student&apos;s home, a rented space, or online via any video conferencing platform (Zoom, Google Meet, WhatsApp, etc.).</li>
    <li><strong>No EdTech Platform Engagement:</strong> You shall not join, teach on, or hold sessions on any competing EdTech platform or online educational service provider, whether established or emerging, without prior express written approval from the Company.</li>
    <li><strong>No Monetised Online Educational Content:</strong> You shall not publish, upload, or monetise educational content on YouTube, Instagram, Telegram, or any other digital platform covering subjects related to your role, without prior written approval.</li>
    <li><strong>Paid and Unpaid Equally Covered:</strong> The above restrictions apply irrespective of whether the outside engagement is paid, unpaid, voluntary, or at a nominal fee. An unpaid arrangement still creates a conflict of interest and risks inadvertent exposure of Company curricula, question banks, and methodology.</li>
  </ul>
  <p><strong>Exception Procedure:</strong> If you wish to engage in any activity covered above, you must submit a written request to the Superadmin/HR detailing: (a) the nature of the engagement, (b) the institution or platform, (c) the subjects and level, and (d) any commercial arrangement. No such engagement may commence without prior express written approval.</p>
  <blockquote style="border-left:4px solid #c0392b;padding:8px 16px;background:#fff5f5;margin:10px 0;font-size:10.5pt;">
    <strong>Consequences of Violation:</strong> Breach of Clause 12.1A may result in: (i) immediate termination without notice or notice pay; (ii) forfeiture of pending bonus and retention chunks; (iii) recovery of training and onboarding costs; and (iv) civil legal action for damages under Indian contract law.
  </blockquote>
  <p style="font-size:10pt;color:#4a5568;"><em>Legal Note: Restrictions during employment on outside teaching and dual employment are valid and enforceable under Indian law as upheld in Niranjan Shankar Golikari v. Century Spinning &amp; Mfg. Co. Ltd. (AIR 1967 SC 1098). Section 27 of the Indian Contract Act, 1872, which voids post-employment restraints, does not apply to during-employment restrictions.</em></p>
  <h3 style="color:#2c5282;margin-top:12px;">12.2 Non-Solicitation (Post-Employment)</h3>
  <p>For a period of <strong>1 (One) year</strong> after your separation from the Company (for any reason), you shall not:</p>
  <ul>
    <li>Solicit, approach, or induce any student, parent, customer, or client of the Company to divert their business to a competitor</li>
    <li>Recruit or solicit any employee or contractor of the Company to leave their employment or engagement with the Company</li>
  </ul>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;font-style:italic;">
    Note: The non-compete clause during employment is a reasonable restriction and is enforceable under Indian law as per Section 27 of the Indian Contract Act, 1872, as it applies only during the term of employment. Post-employment restrictions are subject to applicable law and court interpretation, and are intended as a good-faith commitment from the employee.
  </blockquote>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">13. PERFORMANCE MANAGEMENT</h2>
  <h3 style="color:#2c5282;margin-top:12px;">13.1 Review Cycle</h3>
  <ul>
    <li><strong>Quarterly Check-ins:</strong> Informal performance discussions with your reporting manager every quarter</li>
    <li><strong>Annual Appraisal:</strong> Formal performance review every financial year (April–May cycle)</li>
    <li><strong>KPIs and OKRs:</strong> Measurable objectives will be set mutually within the first 30 days of employment</li>
  </ul>
  <h3 style="color:#2c5282;margin-top:12px;">13.2 Performance Improvement Plan (PIP)</h3>
  <p>If at any review, the Company determines that your performance does not meet the expected standards, you may be placed on a <strong>Performance Improvement Plan (PIP)</strong> for a duration of 45 (Forty-Five) days. During PIP, you will be required to demonstrate measurable improvement. Failure to do so may lead to termination of employment, with the standard notice period / payment in lieu of notice applicable under Clause 14.1–14.2 (this is <strong>not</strong> a "Termination for Cause" summary dismissal under Clause 14.4).</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">14. TERMINATION &amp; NOTICE PERIOD</h2>
  <h3 style="color:#2c5282;margin-top:12px;">14.1 Post-Confirmation Notice Period</h3>
  <p>After successful completion of probation and formal written confirmation:</p>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <thead style="background:#1a3c6e;color:#fff;">
      <tr>
        <th style="${TH};width:38%;">Party</th>
        <th style="${TH}">Notice Period</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="${TDC}"><strong>Employee</strong></td>
        <td style="${TDC}">60 (Sixty) calendar days written notice</td>
      </tr>
      <tr>
        <td style="${TD}"><strong>Company</strong></td>
        <td style="${TD}">30 (Thirty) calendar days written notice</td>
      </tr>
    </tbody>
  </table>
  <h3 style="color:#2c5282;margin-top:12px;">14.2 Payment in Lieu of Notice (PILON)</h3>
  <p>Either party may choose to pay salary in lieu of serving the notice period, subject to mutual agreement or at the Company's sole discretion for Company-initiated separations.</p>
  <h3 style="color:#2c5282;margin-top:12px;">14.3 Garden Leave</h3>
  <p>The Company reserves the right to place you on garden leave during the notice period, during which you will continue to receive your salary but may be relieved of your operational responsibilities and access to systems.</p>
  <h3 style="color:#2c5282;margin-top:12px;">14.4 Termination for Cause</h3>
  <p>The Company reserves the right to terminate employment <strong>immediately and without notice</strong> in the following circumstances:</p>
  <ul>
    <li>Gross misconduct, insubordination, fraud, theft, or dishonesty</li>
    <li>Breach of confidentiality or intellectual property obligations</li>
    <li>Violations of Company's code of conduct or applicable law</li>
    <li>Any act prejudicial to the interest of the Company or its stakeholders</li>
  </ul>
  <p style="font-size:10.5pt;color:#374151;"><em>Note: Termination on grounds of unsatisfactory performance following a Performance Improvement Plan (Clause 13.2) is <strong>not</strong> treated as "Termination for Cause" under this clause. Such separations shall follow the standard notice period / payment in lieu of notice under Clause 14.1–14.2, and do not trigger forfeiture of Retention Bonus chunks under Clause 3.6.</em></p>
  <h3 style="color:#2c5282;margin-top:12px;">14.5 Full and Final Settlement (F&amp;F)</h3>
  <p>Upon separation (for any reason), your F&amp;F settlement shall include:</p>
  <ul>
    <li>Unpaid salary up to the last working day</li>
    <li>Encashment of accrued balance earned leave (as per policy)</li>
    <li>Any confirmed bonus due (pro-rated if applicable)</li>
    <li><strong>Less:</strong> Bond breach compensation (Clause 6.3), relocation bonus recovery (Clause 4), outstanding loans/advances, and any other amounts recoverable from you</li>
  </ul>
  <p>F&amp;F will be processed within 45 (Forty-Five) working days of your last working day, subject to clearance of all Company assets, completion of the exit process, and no outstanding obligations.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">15. STATUTORY COMPLIANCE &amp; EMPLOYEE OBLIGATIONS</h2>
  <p>You are expected to:</p>
  <ul>
    <li>Provide accurate personal, educational, and professional documents at the time of joining. Any misrepresentation shall be grounds for immediate termination.</li>
    <li>Comply with applicable labor laws, regulations, and Company policies at all times</li>
    <li>Declare all other income sources to the Company's payroll team for accurate TDS calculation</li>
    <li>Comply with the applicable provisions of the <strong>Digital Personal Data Protection Act, 2023</strong> while handling student and Company data</li>
    <li>Comply with all directions, circulars, and policies issued by the Company from time to time</li>
  </ul>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">16. COMPANY'S RIGHT TO MODIFY POLICIES</h2>
  <p>The Company reserves the right to amend, modify, supplement, or replace any of its HR policies, compensation structures (subject to statutory minimums), incentive plans, leave policies, and work arrangements at any time, with or without prior notice, as per business requirements. Such changes will be communicated through appropriate internal channels. Your continued employment after such communication shall constitute your acceptance of the changes.</p>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;font-style:italic;">
    This clause is particularly important for PrrepX Pathshala as it enables the Company to evolve its policies in line with our rapid growth from a startup to a mature organization, changes in applicable labor legislation (including upcoming labor codes under India's Labour Code Reforms 2020), and best market practices.
  </blockquote>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">17. GRIEVANCE REDRESSAL</h2>
  <p>In the event of any grievance relating to your employment, compensation, working conditions, or any other service matter, you may:</p>
  <ul>
    <li>Raise the issue informally with your reporting manager in the first instance</li>
    <li>Escalate in writing to HR / HR Manager if unresolved within 5 (Five) working days</li>
    <li>Escalate to the Founder/Managing Director if not resolved at the HR level within 7 (Seven) working days</li>
  </ul>
  <p>The Company is committed to fair, timely, and impartial resolution of all employee grievances. The POSH (Prevention of Sexual Harassment) Act, 2013 framework applies, and the Company shall constitute an Internal Committee (IC) as required upon applicable threshold.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">18. GOVERNING LAW &amp; DISPUTE RESOLUTION</h2>
  <h3 style="color:#2c5282;margin-top:12px;">18.1 Governing Law</h3>
  <p>This offer letter and employment contract shall be governed by and construed in accordance with the laws of India, including applicable central and state labor legislation.</p>
  <h3 style="color:#2c5282;margin-top:12px;">18.2 Dispute Resolution</h3>
  <p>In the event of any dispute, claim, or controversy arising out of or relating to this offer letter, the employment relationship, or any breach thereof, the parties agree to:</p>
  <ol>
    <li>First attempt resolution through <strong>good-faith negotiations</strong> within 15 days of written notice of dispute</li>
    <li>If unresolved, submit the dispute to <strong>mediation</strong> administered under applicable rules</li>
    <li>If mediation fails, resolve by <strong>binding arbitration</strong> under the Arbitration and Conciliation Act, 1996, with a sole arbitrator mutually agreed upon by both parties</li>
    <li>The seat and venue of arbitration shall be <strong>Lucknow, Uttar Pradesh</strong></li>
  </ol>
  <p style="font-size:10pt;color:#4a5568;"><em>Saving Clause: Notwithstanding the above, this arbitration clause shall not apply to, and shall not be construed as ousting, the exclusive statutory jurisdiction of Labour Courts, Industrial Tribunals, or other special fora constituted under the Industrial Disputes Act, 1947 (or its successor, the Industrial Relations Code, 2020) or any other applicable labour legislation, in respect of any employee who qualifies as a "workman" or equivalent protected category under such legislation.</em></p>
  <h3 style="color:#2c5282;margin-top:12px;">18.3 Jurisdiction</h3>
  <p>For matters not covered by arbitration, the <strong>courts at Lucknow, Uttar Pradesh</strong> shall have exclusive jurisdiction.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">19. MISCELLANEOUS PROVISIONS</h2>
  <p><strong>(a) Entire Agreement:</strong> This offer letter, together with any annexures and policies incorporated by reference, constitutes the entire and complete agreement between the parties with respect to your employment and supersedes all prior discussions, representations, and understandings.</p>
  <p><strong>(b) Severability:</strong> If any provision of this letter is held to be invalid, illegal, or unenforceable under applicable law, the remaining provisions shall continue in full force and effect.</p>
  <p><strong>(c) Waiver:</strong> Failure by either party to enforce any provision of this letter shall not constitute a waiver of that provision or of the right to enforce it in the future.</p>
  <p><strong>(d) Background Verification:</strong> This offer is subject to satisfactory completion of a background verification check (educational credentials, employment history, criminal record, reference checks). The Company reserves the right to withdraw this offer or terminate employment if any undisclosed or detrimental information surfaces during or after background verification.</p>
  <p><strong>(e) Physical/Medical Fitness:</strong> You may be required to provide a fitness certificate from a registered medical practitioner upon joining.</p>
  <p><strong>(f) Personal Documents to be Submitted at Joining:</strong></p>
  <ul>
    <li>PAN Card (mandatory for payroll and TDS)</li>
    <li>Aadhaar Card</li>
    <li>Educational qualification certificates</li>
    <li>Relieving/Experience letters (if applicable)</li>
    <li>Last 3 months' salary slips (if applicable)</li>
    <li>Bank account details (IFSC, Account Number, Branch)</li>
    <li>Two recent passport-size photographs</li>
  </ul>
  <p><strong>(g) Equal Opportunity Employer:</strong> PrrepX Pathshala Private Limited is an equal opportunity employer. Recruitment, hiring, and terms of employment are determined solely on the basis of merit, qualifications, and business need, without regard to gender, religion, caste, race, disability, marital status, or any other characteristic protected under applicable Indian law.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;border-bottom:1px solid #1a3c6e;padding-bottom:4px;page-break-before:always;">20. ACCEPTANCE OF OFFER</h2>
  <p>This offer is <strong>valid until {{ACCEPTANCE_DEADLINE}}</strong>. Kindly confirm your acceptance by:</p>
  <ol>
    <li>Signing this offer letter on each page with your full signature</li>
    <li>Countersigning the Acceptance block below</li>
    <li>Returning a <strong>scanned copy</strong> to <strong>admin@prrepxpathshala.live</strong> by the acceptance deadline</li>
  </ol>
  <p>Upon acceptance, further joining formalities, appointment letter, and onboarding documents will be shared with you. Your appointment letter (containing the final, binding terms of employment) will be issued to you <strong>on or after your date of joining</strong> upon completion of formalities.</p>
  <p><strong>Failure to respond by the deadline shall be treated as a rejection of this offer, and the Company shall be free to consider other candidates for the position.</strong></p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>

  <p>We congratulate you once again, {{FIRST_NAME}}, and look forward to having you as a valued partner in PrrepX Pathshala's journey of transforming education in India. Your talent, dedication, and passion are exactly what we look for in our team members, and we are confident that this engagement will be mutually rewarding.</p>
  <p><strong>Welcome to the PrrepX Pathshala family!</strong></p>

  <p><strong>Yours sincerely,</strong></p>
  <p><strong>Hanumat Lal Vishwakarma</strong></p>

  <div style="display:flex;align-items:flex-start;gap:48px;margin-top:8px;">
    <div style="text-align:center;width:200px;">
      <div style="height:90px;display:flex;align-items:flex-end;justify-content:center;">
        <img src="/sign.png" alt="Authorized Signature" style="height:72px;max-width:180px;object-fit:contain;display:block;margin:0 auto 4px;" />
      </div>
      <div style="border-top:1px solid #333;padding-top:4px;font-size:10pt;">
        <strong>Authorized Signatory</strong><br/>
        Hanumat Lal Vishwakarma<br/>
        Founder &amp; Managing Director<br/>
        <span style="font-size:9.5pt;color:#555;">Date: {{SIGN_DATE}}</span>
      </div>
    </div>
    <div style="text-align:center;width:200px;margin-left:56px;">
      <div style="position:relative;height:90px;">
        <img src="/seal.png" alt="Company Seal" style="position:absolute;left:0;right:0;bottom:4px;margin:0 auto;max-height:150px;max-width:190px;width:auto;height:auto;display:block;" />
      </div>
      <div style="border-top:1px solid #333;padding-top:4px;font-size:10pt;">
        <strong>Company Seal</strong>
      </div>
    </div>
  </div>

  <h2 data-pb="1" style="color:#1a3c6e;text-align:center;page-break-before:always;">CANDIDATE ACCEPTANCE DECLARATION</h2>
  <p>I, <strong>{{EMPLOYEE_NAME}}</strong>, hereby acknowledge that I have read, understood, and agree to all the terms and conditions set forth in this Offer Letter, including all sections and annexures herein. I accept the offer of employment at PrrepX Pathshala Private Limited on the terms stated above.</p>
  <p>I confirm that:</p>
  <ul>
    <li>All information furnished by me during the recruitment process is true, accurate, and complete</li>
    <li>I am not bound by any non-compete, restrictive covenant, or notice period obligation from a previous employer that would prevent me from joining PrrepX Pathshala on the agreed date</li>
    <li>I understand and accept the <strong>2-year Service Bond</strong> as described in Section 6 and the financial implications of early departure</li>
    <li>I accept the terms of the Confidentiality, Intellectual Property, and Non-Solicitation clauses</li>
  </ul>
  <p style="background:#f9f9f9;border-left:4px solid #555;padding:10px 14px;font-size:13px;color:#333;margin:16px 0;">
    <strong>Note:</strong> This Offer Letter sets out the key terms of your engagement. For complete, detailed, and updated policies governing all aspects of your employment &mdash; including leave entitlements, attendance rules, conduct standards, probation obligations, and workplace procedures &mdash; please refer to the Company&apos;s <strong>HR Policy Manual</strong> issued to you at the time of joining. Where a matter is not specifically addressed in this Offer Letter, the HR Policy Manual shall govern. Where this Offer Letter expressly sets out a term (such as designation, compensation, or joining date), this Offer Letter shall prevail. Where the HR Policy Manual provides more beneficial terms to you than this Offer Letter, those more beneficial terms shall apply.
  </p>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <tr><td style="${TDLBLC}">Full Name:</td><td style="${TDC}">__________________________</td></tr>
    <tr><td style="${TDLBL}">Signature:</td><td style="${TD}">________________________________</td></tr>
    <tr><td style="${TDLBLC}">Date of Acceptance:</td><td style="${TDC}">________________________________</td></tr>
    <tr><td style="${TDLBL}">Mobile (for verification):</td><td style="${TD}">________________________________</td></tr>
    <tr><td style="${TDLBLC}">Email (for verification):</td><td style="${TDC}">________________________________</td></tr>
  </table>

  <hr style="border:none;border-top:2px solid #1a3c6e;margin:24px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;text-align:center;page-break-before:always;">ANNEXURE A – COMPENSATION BREAKUP SUMMARY SHEET</h2>
  <p><strong>Employee Name:</strong> {{EMPLOYEE_NAME}}<br/>
  <strong>Designation:</strong> {{DESIGNATION}}<br/>
  <strong>Effective Date:</strong> {{DATE_OF_JOINING}}</p>

  <h3 style="color:#2c5282;margin-top:12px;">A.1 Salary &amp; CTC Breakup</h3>
  <table style="width:100%;table-layout:fixed;border-collapse:collapse;margin-bottom:12px;">
    <thead style="background:#1a3c6e;color:#fff;">
      <tr>
        <th style="${TH};width:36%;">Component</th>
        <th style="${THR};width:18%;">Monthly (&#x20B9;)</th>
        <th style="${THR};width:18%;">Annual (&#x20B9;)</th>
        <th style="${TH};width:28%;">Tax Status</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="${TDC}">Basic Salary</td>
        <td style="${TDRCA}">{{S_BASIC_M}}</td>
        <td style="${TDRCA}">{{S_BASIC_A}}</td>
        <td style="${TDC}">Fully Taxable u/s 15 of IT Act, 1961</td>
      </tr>
      <tr>
        <td style="${TD}">HRA</td>
        <td style="${TDRA}">{{S_HRA_M}}</td>
        <td style="${TDRA}">{{S_HRA_A}}</td>
        <td style="${TD}">Partially Exempt u/s 10(13A); for non-metro (Lucknow), exempt portion ≤ 40% of Basic (subject to actual rent paid)</td>
      </tr>
      <tr>
        <td style="${TDC}">Leave Travel Allowance</td>
        <td style="${TDRCA}">{{S_LTA_M}}</td>
        <td style="${TDRCA}">{{S_LTA_A}}</td>
        <td style="${TDC}">Exempt u/s 10(5) on actuals; twice in a block of 4 calendar years; bills required</td>
      </tr>
      <tr>
        <td style="${TD}">Medical Allowance</td>
        <td style="${TDRA}">{{S_MED_M}}</td>
        <td style="${TDRA}">{{S_MED_A}}</td>
        <td style="${TD}">Fully Taxable; ₹15,000 exemption abolished w.e.f. FY 2018-19; covered under Standard Deduction u/s 16(ia)</td>
      </tr>
      <tr>
        <td style="${TDC}">Telephone &amp; Internet Allowance</td>
        <td style="${TDRCA}">{{S_TEL_M}}</td>
        <td style="${TDRCA}">{{S_TEL_A}}</td>
        <td style="${TDC}">Reimbursement-based partial exemption u/s 17(2); excess over actual bills taxable</td>
      </tr>
      <tr>
        <td style="${TD}">Special Allowance</td>
        <td style="${TDRA}">{{S_SPEC_M}}</td>
        <td style="${TDRA}">{{S_SPEC_A}}</td>
        <td style="${TD}">Fully Taxable u/s 15 of IT Act, 1961</td>
      </tr>
      <tr>
        <td style="${TDC};font-style:italic;">Variable Pay / Annual Performance Bonus</td>
        <td style="${TDRCA};font-style:italic;">{{S_VARIABLE_M}}</td>
        <td style="${TDRCA};font-style:italic;">{{S_VARIABLE_A}}</td>
        <td style="${TDC};font-style:italic;">Fully Taxable u/s 15; KPI-linked; paid annually (see Sec. 3.7)</td>
      </tr>
      <tr>
        <td style="${TD};font-style:italic;">Retention Bonus (Fixed, in 5 Equal Chunks)</td>
        <td style="${TDRA};font-style:italic;">&#x2014;</td>
        <td style="${TDRA};font-style:italic;">{{S_ESOP_POOL_ANNEX}}</td>
        <td style="${TD};font-style:italic;">Salary income u/s 15 IT Act; TDS by employer; pool committed in CTC; 5 equal chunks (see Sec. 3.6)</td>
      </tr>
      <tr style="background:#1a3c6e;color:#fff;">
        <td style="${TDTOT};text-align:left;white-space:normal;word-break:break-word;"><strong>Gross Salary – Total</strong></td>
        <td style="${TDTOT}"><strong>{{S_GROSS_M}}</strong></td>
        <td style="${TDTOT}"><strong>{{S_GROSS_A}}</strong></td>
        <td style="padding:8px 10px;border:1px solid #0d2a52;"></td>
      </tr>
      <tr>
        <td style="${TDC};font-style:italic;">Joining / Relocation Bonus (One-Time – Year 1 only)</td>
        <td style="${TDRCA};font-style:italic;">—</td>
        <td style="${TDRCA};font-style:italic;">{{S_RELOC_A}}</td>
        <td style="${TDC};font-style:italic;">Fully Taxable; non-recurring; recoverable if exit &lt; 12 months</td>
      </tr>
      <tr style="background:#2c5282;color:#fff;">
        <td style="${TDTOT2};text-align:left;white-space:normal;word-break:break-word;"><strong>Total CTC (Monthly Gross &times;12 + Variable Pay + Retention Bonus + Joining Bonus)</strong></td>
        <td style="${TDTOT2}">—</td>
        <td style="${TDTOT2}"><strong>{{S_CTC1_A}}</strong></td>
        <td style="padding:8px 10px;border:1px solid #264e82;"></td>
      </tr>
    </tbody>
  </table>

  <h3 style="color:#2c5282;margin-top:12px;">A.2 One-Time Components (Included in Year 1 CTC)</h3>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <thead style="background:#1a3c6e;color:#fff;">
      <tr>
        <th style="${TH};width:28%;">Component</th>
        <th style="${THR};width:18%;">Amount (₹)</th>
        <th style="${TH}">Condition</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="${TD}">Joining / Relocation Bonus</td>
        <td style="${TDR}">{{S_RELOC_A}}</td>
        <td style="${TD}">One-time; included in Year 1 CTC only; disbursed within 30 (Thirty) days of joining; fully taxable; recoverable if exit within 12 months</td>
      </tr>
    </tbody>
  </table>

  <h3 style="color:#2c5282;margin-top:12px;">A.3 Key Statutory Notes</h3>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <thead style="background:#1a3c6e;color:#fff;">
      <tr>
        <th style="${TH};width:38%;">Parameter</th>
        <th style="${TH}">Status</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="${TDC}"><strong>EPF (Employee Provident Fund)</strong></td>
        <td style="${TDC}"><strong>NOT APPLICABLE</strong> currently (Company below 20 employee threshold). Will be enrolled upon statutory obligation.</td>
      </tr>
      <tr>
        <td style="${TD}"><strong>ESIC</strong></td>
        <td style="${TD}">{{S_ESIC_STATUS}}</td>
      </tr>
      <tr>
        <td style="${TDC}"><strong>Gratuity</strong></td>
        <td style="${TDC}">Applicable upon completion of 5 years of continuous service</td>
      </tr>
      <tr>
        <td style="${TD}"><strong>Professional Tax</strong></td>
        <td style="${TD}">Applicable as per state law at place of employment</td>
      </tr>
      <tr>
        <td style="${TDC}"><strong>TDS (Income Tax)</strong></td>
        <td style="${TDC}">Applicable as per applicable Income Tax Act slabs</td>
      </tr>
    </tbody>
  </table>
  <blockquote style="border-left:4px solid #1a3c6e;padding:8px 16px;background:#f8faff;margin:12px 0;">
    <strong>Estimated Monthly Take-Home Salary (Indicative):</strong><br/>
    Gross Monthly Salary: ₹{{S_GROSS_M}}/-<br/>
    Less: TDS (Income Tax): Varies by your chosen tax regime (old/new) and declared investments — use the Income Tax Department's official calculator or consult HR/Payroll for a personalised estimate<br/>
    Less: Professional Tax (state-specific; commonly ~₹200/month in most states): ~₹200/-<br/>
    <strong>Illustrative Net after Professional Tax (before TDS): ₹{{S_NET_AFTER_PT_M}}/-</strong><br/>
    <em>Exact in-hand amount depends on your applicable tax slab, declared investments under Chapter VI-A (80C, 80D, etc.), and state professional tax rates.</em>
  </blockquote>

  <hr style="border:none;border-top:2px solid #1a3c6e;margin:24px 0;"/>

  <h2 data-pb="1" style="color:#1a3c6e;text-align:center;page-break-before:always;">ANNEXURE B – SERVICE AGREEMENT / EMPLOYMENT BOND SUMMARY</h2>
  <p><strong>Employee:</strong> {{EMPLOYEE_NAME}}<br/>
  <strong>Company:</strong> PrrepX Pathshala Private Limited</p>
  <table style="width:100%;border-collapse:collapse;margin-bottom:12px;">
    <thead style="background:#1a3c6e;color:#fff;">
      <tr>
        <th style="${TH};width:38%;">Clause</th>
        <th style="${TH}">Detail</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td style="${TDLBLC}">Bond Period</td>
        <td style="${TDC}">2 (Two) Years from Date of Joining or Confirmation (as specified)</td>
      </tr>
      <tr>
        <td style="${TDLBL}">Bond Breach Compensation</td>
        <td style="${TD}">3 (Three) Months' Gross Monthly Salary = {{BOND_BREACH_AMOUNT}} (full amount payable on breach; no pro‑rata reductions)</td>
      </tr>
      <tr>
        <td style="${TDLBLC}">Early Exit (First Month)</td>
        <td style="${TDC}">Full Relocation Bonus recovery + full Bond Breach Amount (no pro‑rata reductions)</td>
      </tr>
      <tr>
        <td style="${TDLBL}">Bond Waiver</td>
        <td style="${TD}">At Company's sole discretion in compassionate circumstances</td>
      </tr>
      <tr>
        <td style="${TDLBLC}">Recovery Method</td>
        <td style="${TDC}">F&amp;F deduction, legal proceedings, arbitration</td>
      </tr>
      <tr>
        <td style="${TDLBL}">Governing Law</td>
        <td style="${TD}">Indian Contract Act, 1872; applicable Indian labor statutes</td>
      </tr>
    </tbody>
  </table>
  <p>By signing this offer letter, I acknowledge this Service Agreement as a binding contractual obligation forming part of my employment contract.</p>

  <hr style="border:none;border-top:1px solid #ccc;margin:12px 0;"/>
  <p style="font-size:9pt;color:#555;font-style:italic;">This document was prepared in accordance with applicable Indian labor laws, including the Indian Contract Act 1872, Payment of Wages Act 1936, Minimum Wages Act 1948, Maternity Benefit Act 1961, Payment of Gratuity Act 1972, Shops and Establishments Act (State-specific), Income Tax Act 1961, and the Digital Personal Data Protection Act 2023.</p>
  <p style="font-size:9pt;color:#555;font-style:italic;">Issued by PrrepX Pathshala Private Limited. All rights reserved.</p>
  <p style="text-align:center;font-weight:bold;margin-top:16px;">[End of Offer Letter – {{EMPLOYEE_NAME}} | PrrepX Pathshala Private Limited]</p>

</div>`;


// execCommand wrappers (still universally supported for contenteditable)
const fmt = (cmd) => { document.execCommand(cmd, false, null); };
const applyFontSize = (sizeWithUnit, editorEl) => {
  document.execCommand("fontSize", false, "7");
  (editorEl || document.activeElement).querySelectorAll('font[size="7"]').forEach(font => {
    const span = document.createElement("span");
    span.style.fontSize = sizeWithUnit;
    while (font.firstChild) span.appendChild(font.firstChild);
    font.parentNode.replaceChild(span, font);
  });
};
const insertLink = () => {
  const url = window.prompt("Enter URL:", "https://");
  if (url && url.trim()) document.execCommand("createLink", false, url.trim());
};

// ─────────────────────────────────────────────
// ── Fill-details fields (drives the "Fill Details" modal) ───────────────────
const FILL_FIELDS = [
  // Employee Details
  { group: "Employee Details", key: "EMPLOYEE_NAME",       label: "Employee Full Name",          ph: "e.g. Nipun Dayal" },
  { group: "Employee Details", key: "FIRST_NAME",          label: "First Name (for salutation)",  ph: "e.g. Nipun" },
  { group: "Employee Details", key: "EMPLOYEE_EMAIL",      label: "Email Address",               ph: "e.g. name@example.com" },
  { group: "Employee Details", key: "EMPLOYEE_MOBILE",     label: "Mobile Number",               ph: "e.g. +91-9XXXXXXXXX" },
  { group: "Employee Details", key: "EMPLOYEE_ADDRESS",    label: "Mailing Address (1 line)",    ph: "Street, City, State – PIN" },
  // Offer Details
  { group: "Offer Details",    key: "OFFER_DATE",          label: "Offer Date",                  ph: "e.g. 13/03/2026" },
  { group: "Offer Details",    key: "REF_NUMBER",          label: "Reference Number",            ph: "e.g. PPL/HR/2026/0001" },
  { group: "Offer Details",    key: "ACCEPTANCE_DEADLINE", label: "Acceptance Deadline",         ph: "e.g. 20/03/2026" },
  { group: "Offer Details",    key: "SIGN_DATE",           label: "Signature Date (Company copy)", ph: "e.g. 13 March 2026" },
  // Position
  { group: "Position",         key: "DESIGNATION",        label: "Designation / Job Title",     ph: "e.g. Associate Physics Faculty" },
  { group: "Position",         key: "GRADE_LEVEL",        label: "Grade / Level",                ph: "e.g. Associate, Senior Associate, Lead" },
  { group: "Position",         key: "DEPARTMENT",         label: "Department",                  ph: "e.g. Academic – Physics" },
  { group: "Position",         key: "REPORTING_TO",       label: "Reporting To",                ph: "e.g. Raju Vishwas – Co-founder" },
  // Employment
  { group: "Employment",       key: "DATE_OF_JOINING",    label: "Date of Joining",             ph: "e.g. 20 March 2026" },
  { group: "Employment",       key: "WORK_LOCATION",      label: "Work Location",               ph: "e.g. DLF City Office, Lucknow" },
  { group: "Employment",       key: "PROBATION_PERIOD",   label: "Probation Period",            ph: "e.g. 3 (Three) months" },
  // Compensation — gross is a calculator input; joining bonus/variable/retention are always manual
  { group: "Compensation",     key: "GROSS_INPUT",        label: "Target Monthly Gross (₹)",      ph: "e.g. 60000" },
  { group: "Compensation",     key: "JOINING_BONUS_INPUT",label: "Joining / Relocation Bonus (₹)", ph: "e.g. 20000" },
  { group: "Compensation",     key: "VARIABLE_POOL_INPUT",label: "Variable Pay Pool — Annual Max (₹)", ph: "e.g. 80000 or 0" },
  { group: "Compensation",     key: "RETENTION_POOL_INPUT",label: "Retention Bonus Pool — Total (₹)", ph: "e.g. 100000 or 0" },
  { group: "Compensation",     key: "CTC_AMOUNT",         label: "Total CTC",                     ph: "Auto-computed", computed: true },
  { group: "Compensation",     key: "MONTHLY_GROSS",      label: "Gross Salary",                  ph: "Auto-computed from CTC", computed: true },
  { group: "Compensation",     key: "ANNUAL_RECURRING",   label: "Monthly Gross \u00d712 (Recurring)",        ph: "Auto-computed from CTC", computed: true },
  { group: "Compensation",     key: "S_VARIABLE_A",       label: "Annual Variable Pay (KPI)",     ph: "Auto-computed from CTC", computed: true },
  { group: "Compensation",     key: "S_ESOP_A",           label: "Retention Bonus (per chunk)",   ph: "Auto-computed from CTC", computed: true },
  { group: "Compensation",     key: "JOINING_BONUS_AMT",  label: "Joining / Relocation Bonus",   ph: "Auto-computed from CTC", computed: true },
  { group: "Compensation",     key: "BOND_BREACH_AMOUNT", label: "Bond Breach (Training & Content Indemnity, max)", ph: "Auto-computed from CTC", computed: true },
];

const FILL_GROUPS = ["Employee Details", "Offer Details", "Position", "Employment", "Compensation"];

const SAMPLE_FILL_VALUES = (() => {
  const now = new Date();
  // Use IST for all date generation
  const istParts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).formatToParts(now);
  const gp = (type) => istParts.find(p => p.type === type)?.value || '00';
  const yr  = gp('year');
  const mm  = gp('month');
  const dd  = gp('day');
  const hh  = gp('hour');
  const min = gp('minute');
  const ss  = gp('second');
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const monthIdx = parseInt(mm, 10) - 1;
  // Default sample: ₹60,000 monthly gross + ₹20,000 joining bonus, no variable/retention
  const sampleGross = 60000, sampleJoiningBonus = 20000, sampleVariable = 0, sampleRetention = 0;
  const breakdown = computeOfferSalary({ monthlyGross: sampleGross, joiningBonus: sampleJoiningBonus, variablePool: sampleVariable, retentionPool: sampleRetention });
  return {
  EMPLOYEE_NAME:       "Nipun Dayal",
  FIRST_NAME:          "Nipun",
  EMPLOYEE_EMAIL:      "nipun.dayal@gmail.com",
  EMPLOYEE_MOBILE:     "+91-9876543210",
  EMPLOYEE_ADDRESS:    "D-129, Swarnjayantipuram, Ghaziabad, U.P. – 201013",
  OFFER_DATE:          `${dd}/${mm}/${yr}`,
  REF_NUMBER:          `OFFER/HR/${yr}/${mm}${dd}${hh}${min}${ss}`,
  SIGN_DATE:           `${dd} ${months[monthIdx]} ${yr}`,
  ACCEPTANCE_DEADLINE: "20/03/2026",
  DESIGNATION:         "Associate Physics Faculty",
  GRADE_LEVEL:         "Associate",
  DEPARTMENT:          "Academic – Physics",
  REPORTING_TO:        "Raju Vishwas – Co-founder",
  DATE_OF_JOINING:     "20 March 2026",
  WORK_LOCATION:       "DLF City Office, Lucknow",
  PROBATION_PERIOD:    "3 (Three) months",
  GROSS_INPUT:            String(sampleGross),
  JOINING_BONUS_INPUT:    String(sampleJoiningBonus),
  VARIABLE_POOL_INPUT:    String(sampleVariable),
  RETENTION_POOL_INPUT:   String(sampleRetention),
  CTC_AMOUNT:          fmtCTCAmount(parseCTCNumber(breakdown.S_CTC1_A)), // auto-formatted from computed CTC
  ...breakdown, // auto-computed: S_BASIC_M/A, S_HRA_M/A, S_LTA_M/A, S_MED_M/A, S_TEL_M/A, S_SPEC_M/A, S_GROSS_M/A, S_CTC1_A, S_RELOC_A, MONTHLY_GROSS, ANNUAL_RECURRING, BOND_BREACH_AMOUNT
  };
})();


// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
const OfferLetterEditor = () => {
  const { userData } = useAuth();
  const { hasAccess, loading: accessLoading } = useOfferLetterAccess();

  // DOM refs
  const editorRef = useRef(null);
  const previewRef = useRef(null);

  // Load initial content synchronously from localStorage
  const [content, setContent] = useState(() => {
    try {
      const saved = localStorage.getItem("offerLetterDraft_v2");
      if (saved) return JSON.parse(saved).content || DEFAULT_TEMPLATE;
    } catch (_) {}
    return DEFAULT_TEMPLATE;
  });
  const [templateName, setTemplateName] = useState(() => {
    try {
      const saved = localStorage.getItem("offerLetterDraft_v2");
      if (saved) return JSON.parse(saved).name || "Offer Letter – New Employee";
    } catch (_) {}
    return "Offer Letter – New Employee";
  });

  const [showFillModal, setShowFillModal] = useState(false);
  const [fillValues, setFillValues] = useState(SAMPLE_FILL_VALUES);
  const [textColor, setTextColor] = useState("#1a1a1a");
  const [hlColor, setHlColor] = useState("#fff176");
  const textColorRef = useRef(null);
  const hlColorRef = useRef(null);

  const [mode, setMode] = useState("edit"); // "edit" | "preview"
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [wmDataUrl, setWmDataUrl] = useState(null);
  const [showCalcModal, setShowCalcModal] = useState(false);
  const [calcGross, setCalcGross]                 = useState('60000');
  const [calcJoiningBonus, setCalcJoiningBonus]   = useState('20000');
  const [calcVariablePool, setCalcVariablePool]   = useState('0');
  const [calcRetentionPool, setCalcRetentionPool] = useState('0');
  const [calcTab, setCalcTab]   = useState('calc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const [pageInputVal, setPageInputVal] = useState('1');
  const editorWrapperRef = useRef(null);

  // Preload logo as faded canvas for PDF watermark
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const sz = 300;
      const c = document.createElement('canvas');
      c.width = sz; c.height = sz;
      const ctx = c.getContext('2d');
      ctx.globalAlpha = 0.10;
      ctx.drawImage(img, 0, 0, sz, sz);
      setWmDataUrl(c.toDataURL('image/png'));
    };
    img.src = '/logo.jpg';
  }, []);

  // Keep a ref to the latest content so event handlers don't go stale
  const contentRef = useRef(content);
  useEffect(() => { contentRef.current = content; }, [content]);

  // ── Push each h2[data-pb] to the top of the next visual A4 page ──
  const PAGE_H = 1123;
  const enforcePageBreaks = useCallback((containerEl) => {
    if (!containerEl) return;
    const h2s = [...containerEl.querySelectorAll('h2[data-pb]')];
    // Reset padding first so measurements reflect natural flow
    h2s.forEach(h2 => { h2.style.paddingTop = ''; });
    // Use getBoundingClientRect for reliable position relative to container
    const boxTop = containerEl.getBoundingClientRect().top;
    h2s.forEach(h2 => {
      const relTop = Math.round(h2.getBoundingClientRect().top - boxTop);
      const posOnPage = relTop % PAGE_H;
      if (posOnPage > 2) {
        h2.style.paddingTop = (PAGE_H - posOnPage) + 'px';
      }
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Seed contenteditable whenever edit mode is active (initial load + toggle back from preview) ──
  useEffect(() => {
    if (accessLoading || !hasAccess) return;
    if (mode !== 'edit') return;
    if (!editorRef.current) return;
    editorRef.current.innerHTML = contentRef.current;
    requestAnimationFrame(() => enforcePageBreaks(editorRef.current));
  }, [accessLoading, hasAccess, mode, enforcePageBreaks]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-apply page breaks in preview whenever content changes ──
  useEffect(() => {
    if (mode !== 'preview') return;
    const el = previewRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => enforcePageBreaks(el));
    return () => cancelAnimationFrame(id);
  }, [content, mode, enforcePageBreaks]);

  // ── Page tracking: update current/total page on scroll ──
  useEffect(() => {
    const wrapper = editorWrapperRef.current;
    if (!wrapper) return;
    const update = () => {
      const scrollTop  = wrapper.scrollTop;
      const innerEl    = wrapper.querySelector('[class*="previewDoc"]');
      const docHeight  = innerEl ? innerEl.scrollHeight : wrapper.scrollHeight;
      const total      = Math.max(1, Math.ceil(docHeight / PAGE_H));
      const page       = Math.min(total, Math.floor(scrollTop / PAGE_H) + 1);
      setTotalPages(total);
      setCurrentPage(page);
    };
    wrapper.addEventListener('scroll', update, { passive: true });
    update();
    return () => wrapper.removeEventListener('scroll', update);
  }, [mode, hasAccess, accessLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep page input in sync with scroll-derived currentPage ──
  useEffect(() => {
    setPageInputVal(String(currentPage));
  }, [currentPage]);

  const goToPage = (page) => {
    const wrapper = editorWrapperRef.current;
    if (!wrapper) return;
    const target = Math.max(0, (page - 1) * PAGE_H);
    wrapper.scrollTo({ top: target, behavior: 'smooth' });
  };

  // ── Take over scrolling: prevent dashboard-content from scrolling while this page is active ──
  useEffect(() => {
    const el = document.querySelector('.dashboard-content');
    if (!el) return;
    const prevOverflowY = el.style.overflowY;
    const prevPadding   = el.style.padding;
    el.style.overflowY = 'hidden';
    el.style.padding   = '0';
    return () => {
      el.style.overflowY = prevOverflowY;
      el.style.padding   = prevPadding;
    };
  }, []);

  // ── Track edits from contenteditable ──
  const handleEditorInput = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    // Strip artificial padding-top before serializing so it is never saved
    el.querySelectorAll('h2[data-pb]').forEach(h2 => { h2.style.paddingTop = ''; });
    const html = el.innerHTML;
    contentRef.current = html;
    setContent(html);
    // Reapply visual breaks after save
    requestAnimationFrame(() => enforcePageBreaks(el));
  }, [enforcePageBreaks]);

  // ── Apply fill-values to the letter (replace {{KEY}} tokens) ──
  const handleFillSubmit = useCallback(() => {
    let html = contentRef.current;
    Object.entries(fillValues).forEach(([key, val]) => {
      if (val && String(val).trim()) {
        const re = new RegExp(`\\{\\{${key}\\}\\}`, "g");
        html = html.replace(re, String(val).trim());
      }
    });
    if (editorRef.current) editorRef.current.innerHTML = html;
    contentRef.current = html;
    setContent(html);
    setShowFillModal(false);
    toast.success("✅ Details applied to the offer letter!");
  }, [fillValues]);

  // ── Handle fill-detail field changes; recomputes salary when any Compensation input changes ──
  const handleFillValueChange = useCallback((key, value) => {
    setFillValues(prev => {
      const updated = { ...prev, [key]: value };
      if (SALARY_INPUT_KEYS.includes(key)) {
        const grossNum = parseCTCNumber(updated.GROSS_INPUT);
        if (grossNum >= 1000) {
          const breakdown = computeOfferSalary({
            monthlyGross: grossNum,
            joiningBonus: parseCTCNumber(updated.JOINING_BONUS_INPUT),
            variablePool: parseCTCNumber(updated.VARIABLE_POOL_INPUT),
            retentionPool: parseCTCNumber(updated.RETENTION_POOL_INPUT),
          });
          Object.assign(updated, breakdown); // updates S_* and narrative tokens
          updated.CTC_AMOUNT = fmtCTCAmount(parseCTCNumber(breakdown.S_CTC1_A));
        }
      }
      return updated;
    });
  }, []);


  const handleSave = useCallback(() => {
    localStorage.setItem("offerLetterDraft_v2", JSON.stringify({ content, name: templateName }));
    setSavedAt(new Date());
    toast.success("Draft saved locally!");
  }, [content, templateName]);

  // ── Access guard (after all hooks) ──
  if (accessLoading) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"60vh", flexDirection:"column", gap:"16px", color:"#4a5568" }}>
        <div style={{ width:36, height:36, border:"4px solid #e2e8f0", borderTopColor:"#1a3c6e", borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
        <p>Checking access...</p>
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <motion.div
        style={{ textAlign:"center", padding:"80px 24px", color:"#4a5568" }}
        initial={{ opacity:0, scale:0.95 }}
        animate={{ opacity:1, scale:1 }}
      >
        <FaFileAlt style={{ fontSize:"4rem", color:"#e2e8f0", marginBottom:"20px" }} />
        <h2 style={{ color:"#1a3c6e", marginBottom:"8px" }}>Access Restricted</h2>
        <p>You don't have permission to use the Offer Letter Generator.</p>
        <p style={{ fontSize:"0.875rem", color:"#a0aec0", marginTop:"8px" }}>Contact your superadmin to request access.</p>
      </motion.div>
    );
  }

  const handleReset = () => {
    if (window.confirm("Reset to default template? All changes will be lost.")) {
      if (editorRef.current) editorRef.current.innerHTML = DEFAULT_TEMPLATE;
      setContent(DEFAULT_TEMPLATE);
      contentRef.current = DEFAULT_TEMPLATE;
      localStorage.removeItem("offerLetterDraft_v2");
      toast("Template reset to default.", { icon: "🔄" });
    }
  };

  // ── Download as PDF ──
  const downloadPDF = async () => {
    if (mode !== "preview") setMode("preview");
    setIsExporting(true);
    const tid = toast.loading("Generating PDF...");
    try {
      await new Promise(r => setTimeout(r, 500));
      const el = previewRef.current;

      // ── Step 1: Flatten layout (strip artificial h2 padding + hide visual breaks) ──
      // Must happen BEFORE any measurements so the DOM mirrors what html2canvas captures.
      const h2els = [...el.querySelectorAll("h2[data-pb]")];
      h2els.forEach(h2 => { h2.style.paddingTop = ""; });
      el.classList.add(styles.noVisualBreaks);
      // Clamp element width so overflow content cannot expand the canvas beyond the A4 column.
      // Any table wider than the container will be clipped rather than leaking into the PDF margin.
      const savedOverflow = el.style.overflow;
      const savedWidth    = el.style.width;
      el.style.overflow = 'hidden';
      el.style.width    = el.offsetWidth + 'px';
      // Two rAF ticks so the browser fully reflows the flat layout before we measure
      await new Promise(r => requestAnimationFrame(r));
      await new Promise(r => requestAnimationFrame(r));

      // ── Step 2: Measure ALL positions NOW in the flat layout ──
      // html2canvas will snapshot this exact state, so measurements are guaranteed
      // to align pixel-for-pixel with the captured canvas.

      // h2 forced-break positions (offsetTop relative to el since el is position:relative)
      const h2Positions = h2els
        .map(h2 => h2.offsetTop)
        .filter(pos => pos > 20);
      h2Positions.sort((a, b) => a - b);

      // All block-element bottom edges for snapping natural cuts.
      // Use offsetTop + offsetHeight (layout-relative, scroll-independent).
      // We walk up to el to get the position truly relative to the preview container.
      const getRelTop = (node) => {
        let top = 0;
        let cur = node;
        while (cur && cur !== el) {
          top += cur.offsetTop;
          cur = cur.offsetParent;
        }
        return top;
      };
      // Atomic blocks: a page cut must never fall inside one of these (same idea as
      // needsNewPage() in generateHRManual). Only outermost matches are kept, so a <p>
      // inside a <tr> is covered by the row itself.
      const atomicSel = "tr, p, li, blockquote, h3, h4, h5, img";
      const KEEP_WITH_NEXT_PX = 50; // headings must keep at least this much content below them
      const atomicBlocks = [...el.querySelectorAll(atomicSel)]
        .filter(node => {
          const outer = node.parentElement && node.parentElement.closest(atomicSel);
          return !outer || !el.contains(outer);
        })
        .map(node => {
          const top = getRelTop(node);
          const keep = /^H[1-6]$/.test(node.tagName) ? KEEP_WITH_NEXT_PX : 0;
          return { top, bottom: top + node.offsetHeight + keep };
        })
        .filter(b => b.bottom - b.top > 4);

      // Total document height and page width — measured while flat
      const totalDomH  = el.scrollHeight;
      const elOffsetW  = el.offsetWidth;

      // ── Step 3: Capture canvas — exact same flat layout as measurements above ──
      const scale = 1.5;
      const canvas = await html2canvas(el, { scale, useCORS: true, logging: false });

      // Restore visual preview and clamped styles
      el.style.overflow = savedOverflow;
      el.style.width    = savedWidth;
      el.classList.remove(styles.noVisualBreaks);
      requestAnimationFrame(() => enforcePageBreaks(el));

      // ── Step 4: Build page boundary list ──
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const margin    = 6;
      const drawW     = pdf.internal.pageSize.getWidth()  - margin * 2; // ~186 mm
      // Fixed bands on every PDF page (like HR manual): nothing is drawn inside them except footer/header text.
      const PAGE_TOP_MM    = 14;
      const PAGE_BOTTOM_MM = 16;
      const pageW_mm = pdf.internal.pageSize.getWidth();
      const pageH_mm = pdf.internal.pageSize.getHeight();
      const drawH     = pageH_mm - PAGE_TOP_MM - PAGE_BOTTOM_MM; // content window height
      const mmPerPx   = drawW / elOffsetW;   // mm per DOM pixel
      const pageDomH  = drawH / mmPerPx;     // DOM pixels that fill one content window

      // adjustCut: if rawCut lands inside an atomic block, pull it up to that block's top
      // so the whole block moves to the next page. Blocks taller than a page keep the raw cut.
      const MIN_FILL = 0.35; // never leave a page less than 35% full just to avoid a split
      const adjustCut = (rawCut, pageStart) => {
        let cut = Math.round(rawCut);
        for (let guard = 0; guard < 8; guard++) {
          const hit = atomicBlocks.find(b => b.top < cut && cut < b.bottom);
          if (!hit || hit.top <= pageStart + pageDomH * MIN_FILL) break;
          cut = Math.round(hit.top);
        }
        return cut;
      };

      const forcedBreaks = [0, ...h2Positions];
      const allBreaks    = [];
      for (let i = 0; i < forcedBreaks.length; i++) {
        allBreaks.push(forcedBreaks[i]);
        const sectionEnd = i + 1 < forcedBreaks.length ? forcedBreaks[i + 1] : totalDomH;
        let pageStart = forcedBreaks[i];
        while (pageStart + pageDomH < sectionEnd - 2) {
          const cut = adjustCut(pageStart + pageDomH, pageStart);
          if (cut <= pageStart) break;
          allBreaks.push(cut);
          pageStart = cut;
        }
      }

      // Step 5: Slice canvas into pages (blank slices dropped), then draw with header/footer bands
      const pageSlices = [];
      for (let i = 0; i < allBreaks.length; i++) {
        const startDom = allBreaks[i];
        const endDom   = i + 1 < allBreaks.length ? allBreaks[i + 1] : totalDomH;
        const startPx  = Math.round(startDom * scale);
        const heightPx = Math.round((endDom - startDom) * scale);
        if (heightPx <= 0) continue;

        const slice = document.createElement("canvas");
        slice.width  = canvas.width;
        slice.height = heightPx;
        const ctx = slice.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, startPx, canvas.width, heightPx, 0, 0, canvas.width, heightPx);

        const sliceH = (heightPx / canvas.width) * drawW;

        // Full-scan blank check (every 2nd px): a sparse sample could drop a page holding one thin line of text.
        let isBlank = false;
        try {
          const img = ctx.getImageData(0, 0, slice.width, slice.height).data;
          isBlank = true;
          for (let p = 0; p < img.length; p += 8) {
            if (img[p + 3] > 16 && (img[p] < 245 || img[p + 1] < 245 || img[p + 2] < 245)) { isBlank = false; break; }
          }
        } catch (err) {
          isBlank = false;
        }
        if (isBlank) continue;

        pageSlices.push({ dataUrl: slice.toDataURL("image/jpeg", 0.93), sliceH });
      }

      const totalPdfPages = pageSlices.length;
      pageSlices.forEach((pg, idx) => {
        if (idx > 0) pdf.addPage();
        pdf.addImage(pg.dataUrl, "JPEG", margin, PAGE_TOP_MM, drawW, pg.sliceH);

        // Watermark: centered on this A4 page
        if (wmDataUrl) {
          const wmMM = 72;
          pdf.addImage(wmDataUrl, 'PNG', (pageW_mm - wmMM) / 2, (pageH_mm - wmMM) / 2, wmMM, wmMM);
        }

        // Header band (pages 2+; page 1 carries the letterhead) and footer band on every page
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.3);
        if (idx > 0) {
          pdf.text('PrrepX Pathshala Pvt. Ltd.', margin + 2, 8);
          pdf.text('Offer Letter', pageW_mm - margin - 2, 8, { align: 'right' });
          pdf.line(margin + 2, 10, pageW_mm - margin - 2, 10);
        }
        pdf.line(margin + 2, pageH_mm - PAGE_BOTTOM_MM + 2, pageW_mm - margin - 2, pageH_mm - PAGE_BOTTOM_MM + 2);
        pdf.text('Strictly Private & Confidential', margin + 2, pageH_mm - 8);
        pdf.text(`Page ${idx + 1} of ${totalPdfPages}`, pageW_mm - margin - 2, pageH_mm - 8, { align: 'right' });
      });

      const _pdfFirstName = (fillValues.EMPLOYEE_NAME || "Employee").trim().split(" ")[0];
      const _pdfNow = new Date();
      const _pdfDate = _pdfNow.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '');
      pdf.save(`Offer_Letter_${_pdfFirstName}_${_pdfDate}.pdf`);
      toast.success("PDF downloaded!", { id: tid });
    } catch (err) {
      console.error(err);
      toast.error("PDF generation failed.", { id: tid });
    }
    setIsExporting(false);
  };

  // ── Download as DOCX (via docx.js in-browser) ──
  const downloadDOCX = async () => {
    setIsExporting(true);
    const tid = toast.loading("Generating DOCX...");
    try {
      // Convert Quill HTML to plain text blob with .docx extension
      // For a true DOCX we create an HTML blob wrapped in Word XML
      const htmlContent = `
        <!DOCTYPE html>
        <html xmlns:o='urn:schemas-microsoft-com:office:office'
              xmlns:w='urn:schemas-microsoft-com:office:word'
              xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'/>
          <title>${templateName}</title>
          <style>
            body { font-family: 'Times New Roman', serif; font-size: 12pt; margin: 2cm; }
            h1, h2, h3 { color: #1a3c6e; }
            h2[data-pb] { page-break-before: always; break-before: page; }
            table { border-collapse: collapse; width: 100%; }
            table td, table th { border: 1px solid #ccc; padding: 6px 10px; }
            thead { background: #1a3c6e; color: white; }
          </style>
        </head>
        <body>${content}</body>
        </html>`;
      const blob = new Blob(['\ufeff', htmlContent], {
        type: "application/msword",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const _docFirstName = (fillValues.EMPLOYEE_NAME || "Employee").trim().split(" ")[0];
      const _docNow = new Date();
      const _docDate = _docNow.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '');
      a.download = `Offer_Letter_${_docFirstName}_${_docDate}.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("DOCX downloaded!", { id: tid });
    } catch (err) {
      toast.error("DOCX generation failed.", { id: tid });
    }
    setIsExporting(false);
  };

  const handlePrint = () => {
    setMode("preview");
    setTimeout(() => window.print(), 300);
  };

  return (
    <>
    <div
      className={`${styles.container} ${isFullscreen ? styles.fullscreen : ""}`}
    >
      {/* ── HEADER ── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <FaFileAlt className={styles.headerIcon} />
          <h1 className={styles.title}>Offer Letter</h1>
          <span className={styles.headerBrandDivider} />
        </div>

        {/* Document name — centre column, underline-edit style */}
        <div className={styles.docNameRow}>
          <input
            className={styles.docNameInput}
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            placeholder="Untitled document..."
          />
        </div>

        <div className={styles.headerRight}>
          {savedAt && (
            <span className={styles.savedBadge}>
              <FaCheckCircle /> Saved {savedAt.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true }) + ' (IST)'}
            </span>
          )}

          {/* ── Page indicator + jump ── */}
          <div className={styles.pageIndicator}>
            <button
              className={styles.pageNavBtn}
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
              title="Previous page"
            >&#8249;</button>
            <input
              className={styles.pageInput}
              type="text"
              inputMode="numeric"
              value={pageInputVal}
              title="Type a page number and press Enter"
              onChange={e => setPageInputVal(e.target.value.replace(/[^0-9]/g, ''))}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  const n = parseInt(pageInputVal, 10);
                  if (!isNaN(n) && n >= 1 && n <= totalPages) goToPage(n);
                  else setPageInputVal(String(currentPage));
                  e.target.blur();
                }
              }}
              onFocus={e => e.target.select()}
              onBlur={() => setPageInputVal(String(currentPage))}
            />
            <span className={styles.pageTotalLabel}>/ {totalPages}</span>
            <button
              className={styles.pageNavBtn}
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
              title="Next page"
            >&#8250;</button>
          </div>

          <div className={styles.iconBtnGroup}>
            <button className={styles.iconBtn} onClick={handlePrint} title="Print">
              <FaPrint />
            </button>
            <button
              className={styles.iconBtn}
              onClick={() => setIsFullscreen(!isFullscreen)}
              title="Toggle fullscreen"
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
          </div>
        </div>
      </div>

      {/* ── TOOLBAR ROW ── */}
      <div className={styles.toolbarRow}>
        {/* Mode switcher */}
        <div className={styles.modeSwitcher}>
          <button
            className={`${styles.modeBtn} ${mode === "edit" ? styles.modeActive : ""}`}
            onClick={() => setMode("edit")}
          >
            <FaEdit /> Edit
          </button>
          <button
            className={`${styles.modeBtn} ${mode === "preview" ? styles.modeActive : ""}`}
            onClick={() => setMode("preview")}
          >
            <FaEye /> Preview
          </button>
        </div>

        <div className={styles.toolbarActions}>
          {/* Fill Details */}
          <button
            className={styles.fillBtn}
            onClick={() => setShowFillModal(true)}
            title="Fill employee details into the template"
          >
            <FaClipboardList /> Fill Details
          </button>

          {/* Salary Calculator & Policy Guide */}
          <button
            className={styles.calcBtn}
            onClick={() => setShowCalcModal(true)}
            title="Salary Calculator & Compensation Policy Guide (Admin Reference)"
          >
            <FaCalculator /> Calculator
          </button>

          <button className={styles.saveBtn} onClick={handleSave}>
            <FaSave /> Save Draft
          </button>
          <button className={styles.resetBtn} onClick={handleReset}>
            <FaUndo /> Reset
          </button>
        </div>

        {/* Export buttons */}
        <div className={styles.exportGroup}>
          <button
            className={`${styles.exportBtn} ${styles.docxBtn}`}
            onClick={downloadDOCX}
            disabled={isExporting}
          >
            <FaFileWord /> Download DOCX
          </button>
          <button
            className={`${styles.exportBtn} ${styles.pdfBtn}`}
            onClick={downloadPDF}
            disabled={isExporting}
          >
            <FaFilePdf /> Download PDF
          </button>
        </div>
      </div>


      {/* ── RICH FORMAT TOOLBAR (edit mode only) ── */}
      {mode === "edit" && (
        <div className={styles.richToolbar}>

          {/* Undo / Redo */}
          <div className={styles.fmtGroup}>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("undo");}} title="Undo (Ctrl+Z)"><FaUndo /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("redo");}} title="Redo (Ctrl+Y)"><FaRedo /></button>
          </div>
          <div className={styles.fmtDivider}/>

          {/* Paragraph / Heading style */}
          <div className={styles.fmtGroup}>
            <select className={styles.fmtSelect} title="Paragraph style"
              onChange={e=>{document.execCommand("formatBlock",false,e.target.value);}}>
              <option value="p">Normal</option>
              <option value="h1">H1 – Title</option>
              <option value="h2">H2 – Section</option>
              <option value="h3">H3 – Sub</option>
              <option value="h4">H4</option>
              <option value="blockquote">Quote</option>
              <option value="pre">Code</option>
            </select>
          </div>
          <div className={styles.fmtDivider}/>

          {/* Font family */}
          <div className={styles.fmtGroup}>
            <select className={`${styles.fmtSelect} ${styles.fontFamilySelect}`}
              title="Font family"
              onChange={e=>document.execCommand("fontName",false,e.target.value)}>
              {["Times New Roman","Arial","Calibri","Georgia","Verdana","Courier New","Garamond","Trebuchet MS"].map(f=>(
                <option key={f} value={f} style={{fontFamily:f}}>{f}</option>
              ))}
            </select>
          </div>
          <div className={styles.fmtDivider}/>

          {/* Font size */}
          <div className={styles.fmtGroup}>
            <select className={`${styles.fmtSelect} ${styles.fontSizeSelect}`}
              title="Font size"
              onChange={e=>{if(e.target.value) applyFontSize(e.target.value+"pt", editorRef.current);}}>
              <option value="">Size</option>
              {[8,9,10,11,12,14,16,18,20,22,24,28,32,36,48,72].map(s=>(
                <option key={s} value={s}>{s}pt</option>
              ))}
            </select>
          </div>
          <div className={styles.fmtDivider}/>

          {/* B I U S Super Sub */}
          <div className={styles.fmtGroup}>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("bold");}} title="Bold (Ctrl+B)"><FaBold /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("italic");}} title="Italic (Ctrl+I)"><FaItalic /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("underline");}} title="Underline (Ctrl+U)"><FaUnderline /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("strikeThrough");}} title="Strikethrough"><FaStrikethrough /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("superscript");}} title="Superscript"><FaSuperscript /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("subscript");}} title="Subscript"><FaSubscript /></button>
          </div>
          <div className={styles.fmtDivider}/>

          {/* Text colour / Highlight */}
          <div className={styles.fmtGroup}>
            <button className={styles.colorBtn} title="Text colour" onClick={()=>textColorRef.current?.click()}>
              <FaPaintBrush />
              <span className={styles.colorBar} style={{background:textColor}}/>
              <input ref={textColorRef} type="color" value={textColor}
                onChange={e=>{setTextColor(e.target.value);document.execCommand("foreColor",false,e.target.value);}}
                className={styles.hiddenColorInput}/>
            </button>
            <button className={styles.colorBtn} title="Highlight colour" onClick={()=>hlColorRef.current?.click()}>
              <FaHighlighter />
              <span className={styles.colorBar} style={{background:hlColor}}/>
              <input ref={hlColorRef} type="color" value={hlColor}
                onChange={e=>{setHlColor(e.target.value);document.execCommand("hiliteColor",false,e.target.value);}}
                className={styles.hiddenColorInput}/>
            </button>
          </div>
          <div className={styles.fmtDivider}/>

          {/* Alignment */}
          <div className={styles.fmtGroup}>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("justifyLeft");}} title="Align Left"><FaAlignLeft /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("justifyCenter");}} title="Center"><FaAlignCenter /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("justifyRight");}} title="Align Right"><FaAlignRight /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("justifyFull");}} title="Justify"><FaAlignJustify /></button>
          </div>
          <div className={styles.fmtDivider}/>

          {/* Lists + Indent */}
          <div className={styles.fmtGroup}>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("insertUnorderedList");}} title="Bullet List"><FaListUl /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("insertOrderedList");}} title="Numbered List"><FaListOl /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("outdent");}} title="Outdent"><FaOutdent /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("indent");}} title="Indent"><FaIndent /></button>
          </div>
          <div className={styles.fmtDivider}/>

          {/* Link + Clear Formatting */}
          <div className={styles.fmtGroup}>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();insertLink();}} title="Insert Link"><FaLink /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("unlink");}} title="Remove Link"><FaUnlink /></button>
            <button className={styles.fmtBtn} onMouseDown={e=>{e.preventDefault();document.execCommand("removeFormat");}} title="Clear Formatting"><FaEraser /></button>
          </div>

        </div>
      )}

      {/* ── EDITOR / PREVIEW ── */}
      <div className={styles.editorWrapper} ref={editorWrapperRef}>
        {mode === "edit" ? (
          <div className={styles.previewContainer}>
            <div className={styles.pageWrapper}>
              {/* Watermark: one logo centered on each A4 page */}
              <div style={{position:'absolute',inset:0,pointerEvents:'none',zIndex:0,overflow:'hidden',userSelect:'none'}}>
                {Array.from({length:25},(_,i)=>{
                  const WM=300, top=i*PAGE_H+(PAGE_H-WM)/2, left=(794-WM)/2;
                  return <img key={i} src="/logo.jpg" alt="" style={{position:'absolute',width:WM,height:WM,top,left,opacity:0.07,objectFit:'contain',pointerEvents:'none'}} />;
                })}
              </div>
              <div
                ref={editorRef}
                className={styles.previewDoc}
                contentEditable={true}
                suppressContentEditableWarning={true}
                onInput={handleEditorInput}
                style={{ outline: "none", cursor: "text", position:'relative', zIndex:1 }}
              />
            </div>
          </div>
        ) : (
          <div
            className={styles.previewContainer}
          >
            <div className={styles.pageWrapper}>
              {/* Watermark: one logo centered on each A4 page */}
              <div style={{position:'absolute',inset:0,pointerEvents:'none',zIndex:0,overflow:'hidden',userSelect:'none'}}>
                {Array.from({length:25},(_,i)=>{
                  const WM=300, top=i*PAGE_H+(PAGE_H-WM)/2, left=(794-WM)/2;
                  return <img key={i} src="/logo.jpg" alt="" style={{position:'absolute',width:WM,height:WM,top,left,opacity:0.07,objectFit:'contain',pointerEvents:'none'}} />;
                })}
              </div>
              <div
                className={styles.previewDoc}
                ref={previewRef}
                dangerouslySetInnerHTML={{ __html: content }}
                style={{position:'relative', zIndex:1}}
              />
            </div>
          </div>
        )}
      </div>

    </div>

    {/* ── SALARY CALCULATOR & POLICY GUIDE MODAL ── */}
    {showCalcModal && (() => {
      const calcGrossNum = parseCTCNumber(calcGross);
      const calcBD = calcGrossNum >= 1000 ? computeOfferSalary({
        monthlyGross: calcGrossNum,
        joiningBonus: parseCTCNumber(calcJoiningBonus),
        variablePool: parseCTCNumber(calcVariablePool),
        retentionPool: parseCTCNumber(calcRetentionPool),
      }) : null;
      return (
        <div
          className={styles.modalOverlay}
          onClick={e => { if (e.target === e.currentTarget) setShowCalcModal(false); }}
        >
          <div className={`${styles.modalBox} ${styles.calcModalBox}`}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}><FaCalculator /> Salary Calculator &amp; Policy Guide</h2>
              <button className={styles.modalClose} onClick={() => setShowCalcModal(false)} title="Close"><FaTimes /></button>
            </div>

            {/* Tabs */}
            <div className={styles.calcTabs}>
              <button className={`${styles.calcTab} ${calcTab === 'calc' ? styles.calcTabActive : ''}`} onClick={() => setCalcTab('calc')}>
                🔢 Salary Calculator
              </button>
              <button className={`${styles.calcTab} ${calcTab === 'policy' ? styles.calcTabActive : ''}`} onClick={() => setCalcTab('policy')}>
                📋 Policy &amp; Rules Summary
              </button>
            </div>

            {/* ── CALCULATOR TAB ── */}
            {calcTab === 'calc' && (
              <div className={styles.calcBody}>
                <div className={styles.calcInputRow}>
                  <label className={styles.calcInputLabel}>Target Monthly Gross (₹)</label>
                  <input
                    className={styles.calcInput}
                    type="text"
                    placeholder="e.g. 60000"
                    value={calcGross}
                    onChange={e => setCalcGross(e.target.value)}
                    autoFocus
                  />
                </div>
                <div className={styles.calcInputRow}>
                  <label className={styles.calcInputLabel}>Joining / Relocation Bonus (₹)</label>
                  <input className={styles.calcInput} type="text" placeholder="e.g. 20000" value={calcJoiningBonus} onChange={e => setCalcJoiningBonus(e.target.value)} />
                </div>
                <div className={styles.calcInputRow}>
                  <label className={styles.calcInputLabel}>Variable Pay Pool — Annual Max (₹)</label>
                  <input className={styles.calcInput} type="text" placeholder="e.g. 80000 or 0" value={calcVariablePool} onChange={e => setCalcVariablePool(e.target.value)} />
                </div>
                <div className={styles.calcInputRow}>
                  <label className={styles.calcInputLabel}>Retention Bonus Pool — Total (₹)</label>
                  <input className={styles.calcInput} type="text" placeholder="e.g. 100000 or 0" value={calcRetentionPool} onChange={e => setCalcRetentionPool(e.target.value)} />
                </div>
                <p className={styles.pctHint}>
                  Basic/HRA/LTA/Medical/Telephone/Special are auto-calculated from the target gross. Joining Bonus,
                  Variable Pay Pool and Retention Bonus Pool are always entered manually — no CTC-tier formula.
                </p>

                {!calcBD ? (
                  <div className={styles.breakdownEmpty}>
                    <span className={styles.breakdownEmptyIcon}>💰</span>
                    <span>Enter a target monthly gross above (minimum ₹1,000) to see the full salary breakdown.</span>
                  </div>
                ) : (
                  <>
                    <table className={styles.breakdownTable}>
                      <thead>
                        <tr>
                          <th>Component</th>
                          <th>Monthly (₹)</th>
                          <th>Annual / Total (₹)</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ['Basic Salary',      calcBD.S_BASIC_M, calcBD.S_BASIC_A,  '50% of Monthly Gross'],
                          ['HRA',               calcBD.S_HRA_M,   calcBD.S_HRA_A,   '40% of Basic'],
                          ['LTA',               calcBD.S_LTA_M,   calcBD.S_LTA_A,   'Fixed slab by grade'],
                          ['Medical Allowance', calcBD.S_MED_M,   calcBD.S_MED_A,   'Fixed slab by grade'],
                          ['Tel. & Internet',   calcBD.S_TEL_M,   calcBD.S_TEL_A,   'Fixed slab by grade'],
                          ['Special Allowance', calcBD.S_SPEC_M,  calcBD.S_SPEC_A,  'Monthly Gross − others (residual)'],
                        ].map(([label, m, a, note]) => (
                          <tr key={label}><td>{label}</td><td>{m}</td><td>{a}</td><td className={styles.calcNoteCell}>{note}</td></tr>
                        ))}
                        <tr className={styles.breakdownTotalRow}>
                          <td>Gross Salary</td><td>{calcBD.S_GROSS_M}</td><td>{calcBD.S_GROSS_A}</td>
                          <td className={styles.calcNoteCell}>Monthly × 12</td>
                        </tr>
                        <tr className={calcBD.S_VARIABLE_A !== '—' ? styles.breakdownVarRow : styles.breakdownNaRow}>
                          <td><strong>Variable Pay</strong><br/><span style={{fontSize:'0.8em',color:'#b45309'}}>(Performance-based, Annual)</span></td>
                          <td>—</td>
                          <td>{calcBD.S_VARIABLE_A !== '—' ? `₹${calcBD.S_VARIABLE_A}` : '—'}<br/><span style={{fontSize:'0.8em',color:'#555'}}>{calcBD.S_VARIABLE_A !== '—' ? '(max pool)' : ''}</span></td>
                          <td className={styles.calcNoteCell}>{calcBD.S_VARIABLE_A !== '—'
                            ? `Pool ₹${calcBD.S_VARIABLE_A}/yr → actual = KPI% × pool; paid end of FY (April)`
                            : 'Not entered for this offer'}</td>
                        </tr>
                        <tr className={calcBD.S_ESOP_POOL !== '—' ? styles.breakdownEsopRow : styles.breakdownNaRow}>
                          <td><strong>Retention Bonus</strong><br/><span style={{fontSize:'0.8em',color:'#1d4ed8'}}>(Fixed, in 5 Equal Chunks)</span></td>
                          <td>—</td>
                          <td>₹{calcBD.S_ESOP_POOL !== '—' ? calcBD.S_ESOP_POOL : '—'}<br/><span style={{fontSize:'0.8em',color:'#555'}}>{calcBD.S_ESOP_POOL !== '—' ? '(full committed pool)' : ''}</span></td>
                          <td className={styles.calcNoteCell}>{calcBD.S_ESOP_POOL !== '—'
                            ? `Total pool ₹${calcBD.S_ESOP_POOL} → 5 equal annual chunks × ₹${calcBD.S_ESOP_T1}`
                            : 'Not entered for this offer'}</td>
                        </tr>
                        <tr className={styles.breakdownRelocRow}>
                          <td><strong>Joining Bonus</strong><br/><span style={{fontSize:'0.8em',color:'#92400e'}}>(One-time, Year 1 only)</span></td><td>—</td><td>₹{fmtINR(parseCTCNumber(calcJoiningBonus))}</td><td className={styles.calcNoteCell}>Paid within 30–60 days of joining; not recurring</td>
                        </tr>
                        <tr className={styles.breakdownCtcRow}>
                          <td><strong>Total CTC</strong></td><td>—</td><td>{calcBD.S_CTC1_A}</td><td className={styles.calcNoteCell}>Monthly Gross ×12 + Variable Pay + Retention Bonus + Joining Bonus</td>
                        </tr>
                      </tbody>
                    </table>

                    <p className={styles.breakdownNote}>
                      <strong>Bond Breach (Training &amp; Content Indemnity, max):</strong> ₹{fmtINR(parseCTCNumber(calcBD.S_GROSS_M) * 3)} &nbsp;(3 mo. Gross Monthly Salary; full amount payable on breach)
                      {calcBD.S_VARIABLE_A !== '—' && <>
                        <br/><strong>Variable Pay pool (max):</strong> ₹{calcBD.S_VARIABLE_A}/yr &nbsp;|&nbsp; Actual release = Performance KPI% × pool (set by HR/management; default 0% at joining)
                      </>}
                      {calcBD.S_ESOP_POOL !== '—' && <>
                        <br/><strong>Retention Bonus total pool:</strong> ₹{calcBD.S_ESOP_POOL} &nbsp;|&nbsp; Chunk 1: ₹{calcBD.S_ESOP_T1} &nbsp;|&nbsp; Chunk 2: ₹{calcBD.S_ESOP_T2} &nbsp;|&nbsp; Chunk 3: ₹{calcBD.S_ESOP_T3} &nbsp;|&nbsp; Chunk 4: ₹{calcBD.S_ESOP_T4} &nbsp;|&nbsp; Chunk 5: ₹{calcBD.S_ESOP_T5}
                      </>}
                    </p>
                    {calcGrossNum > 0 && calcGrossNum < 21000 && (
                      <p className={styles.breakdownNote} style={{ color: '#b45309', fontWeight: 600 }}>
                        ⚠️ ESIC applicable — monthly gross is at or below the ₹21,000 wage ceiling (ESIC Act, 1948).
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* ── POLICY SUMMARY TAB ── */}
            {calcTab === 'policy' && (
              <div className={styles.calcBody}>

                <h3 className={styles.calcPolicyHeading}>Compensation Model</h3>
                <p className={styles.breakdownNote}>
                  There are no CTC tiers or bands. Basic/HRA/LTA/Medical/Telephone/Special are calculated from a single
                  target Monthly Gross (below). Joining Bonus, Variable Pay Pool and Retention Bonus Pool are always
                  entered manually per offer — negotiated case-by-case. Total CTC = Annual Gross + Variable Pool +
                  Retention Pool + Joining Bonus.
                </p>

                <h3 className={styles.calcPolicyHeading}>Component Calculation Rules &amp; Payment Frequency</h3>
                <table className={`${styles.breakdownTable} ${styles.calcPolicyTable}`}>
                  <thead>
                    <tr><th>Component</th><th>Calculation Rule</th><th>Paid When</th><th>Tax Status</th></tr>
                  </thead>
                  <tbody>
                    {[
                      ['Basic Salary',      '50% × Monthly Gross',             'Every month',                      'Fully taxable u/s 15'],
                      ['HRA',              '40% × Basic Salary (non-metro rate — Lucknow; fully exempt u/s 10(13A) with rent receipts)',  'Every month',  'Fully exempt u/s 10(13A) up to 40% Basic (non-metro); submit rent receipts'],
                      ['LTA',              'Fixed slab: ₹500/1,000/1,500/3,000 (by gross grade)',           'Every month (claim biennial)',      'Exempt u/s 10(5) – twice per 4-yr block, bills reqd'],
                      ['Medical Allowance', 'Fixed slab: ₹600/1,000/1,250/2,000 (by gross grade)',          'Every month',                      'Fully taxable'],
                      ['Telephone & Internet Allowance', 'Fixed slab: ₹600/1,000/1,250/2,500 (by gross grade)', 'Every month', 'Exempt on actual bills; excess taxable u/s 17(2)'],
                      ['Special Allowance', 'Monthly Gross − Basic − HRA − LTA − Medical − Tel. (residual)', 'Every month', 'Fully taxable u/s 15'],
                      ['Variable Pay', `Manually agreed Annual Max Pool (negotiated per offer). Actual release = pool × Performance KPI% (0–100%) set by HR/management after annual review. Default KPI at joining = 0%.`, 'Once/year – end of FY (April)', 'Salary income u/s 15 IT Act; TDS by employer in month of release'],
                      ['Retention Bonus', 'Manually agreed Total Pool (negotiated per offer); disbursed as 5 equal annual cash chunks (Year 1–5; not KPI-linked)', 'End of Year 1 / 2 / 3 / 4 / 5', 'Salary income u/s 15 IT Act; TDS by employer at source'],
                      ['Joining Bonus',    'Manually agreed one-time amount (negotiated per offer)',                  'Once – 30–60 days of joining',     'Fully taxable; recoverable if exit < 12 months'],
                    ].map(([comp, rule, when, tax]) => (
                      <tr key={comp}>
                        <td><strong>{comp}</strong></td>
                        <td className={styles.calcNoteCell}>{rule}</td>
                        <td className={styles.calcNoteCell} style={{whiteSpace:'nowrap'}}>{when}</td>
                        <td className={styles.calcNoteCell}>{tax}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <h3 className={styles.calcPolicyHeading}>LTA / Medical / Telephone Slabs (by Monthly Gross)</h3>
                <table className={`${styles.breakdownTable} ${styles.calcPolicyTable}`}>
                  <thead><tr><th>Monthly Gross Range</th><th>LTA</th><th>Medical</th><th>Telephone</th></tr></thead>
                  <tbody>
                    {[
                      ['&lt; ₹25,000',           '₹500',   '₹600',   '₹600'],
                      ['₹25,000 – ₹49,999',     '₹1,000', '₹1,000', '₹1,000'],
                      ['₹50,000 – ₹99,999',     '₹1,500', '₹1,250', '₹1,250'],
                      ['≥ ₹1,00,000',            '₹3,000', '₹2,000', '₹2,500'],
                    ].map(([range, ...vals]) => (
                      <tr key={range}><td dangerouslySetInnerHTML={{__html: range}} />{vals.map((v, i) => <td key={i}>{v}</td>)}</tr>
                    ))}
                  </tbody>
                </table>

                <div className={styles.calcPolicyNote}>
                  <strong>Variable Pay:</strong> Performance-linked annual bonus. Maximum pool committed in CTC from day one (10% for 12–20 LPA; 8% for 20–30 LPA; 10% for &gt;30 LPA). Actual cash released each April = Pool × Performance KPI% as set by HR/management. Not paid monthly — not reflected in regular payslips. Employees at CTC ≤ ₹12 LPA have no variable pay. Taxable as salary income u/s 15 IT Act.
                </div>
                <div className={styles.calcPolicyNote}>
                  <strong>Retention Bonus:</strong> Fixed pool committed in CTC at hire = 5 × per-chunk amount. Disbursed in 5 equal cash chunks (20% of pool each) at the end of Year 1, 2, 3, 4, and 5. <em>Not KPI-linked</em> — guaranteed subject only to continued employment on each anniversary. Unreleased chunks forfeited on exit. Taxable as salary income u/s 15 IT Act; TDS by employer.
                </div>
                <div className={styles.calcPolicyNote}>
                  <strong>Bond Breach (Training &amp; Content Indemnity):</strong> Maximum = 3 × Gross Monthly Salary. Joining Bonus recovery is separate (Clause 4/6.5), not added here. The full Bond Breach Amount is payable on breach at any time before completion of the Bond Period. Recoverable via F&amp;F deduction, legal proceedings, or arbitration (Lucknow jurisdiction).
                </div>
                <div className={styles.calcPolicyNote}>
                  <strong>EPF:</strong> Not applicable currently (below 20-employee threshold). Will enroll on statutory obligation.&nbsp;
                  <strong>ESIC:</strong> Not applicable for roles where gross salary exceeds ₹21,000/month (current ESIC wage ceiling). Reassessed if Government revises threshold or salary falls below ceiling.
                </div>
              </div>
            )}

          </div>
        </div>
      );
    })()}

    {/* ── FILL DETAILS MODAL (position:fixed, renders above everything) ── */}
    {showFillModal && (
      <div
        className={styles.modalOverlay}
        onClick={e => { if (e.target === e.currentTarget) setShowFillModal(false); }}
      >
        <div className={styles.modalBox}>
          <div className={styles.modalHeader}>
            <h2 className={styles.modalTitle}><FaClipboardList /> Fill Offer Letter Details</h2>
            <button className={styles.modalClose} onClick={() => setShowFillModal(false)} title="Close">
              <FaTimes />
            </button>
          </div>

          <div className={styles.modalLayout}>

            {/* ── LEFT: form fields ── */}
            <div className={styles.modalFormPane}>
              {FILL_GROUPS.map(group => (
                <div key={group} className={styles.modalSection}>
                  <h3 className={styles.modalSectionTitle}>{group}</h3>
                  <div className={styles.modalGrid}>
                    {FILL_FIELDS.filter(f => f.group === group).map(f => (
                      <div key={f.key} className={styles.modalField}>
                        <label className={styles.modalLabel}>
                          {f.label}
                          {f.computed && (
                            <span style={{fontSize:'0.65rem',background:'#10b981',color:'#fff',borderRadius:3,padding:'1px 5px',fontWeight:700,letterSpacing:'0.05em',textTransform:'uppercase'}}>auto</span>
                          )}
                          <span className={styles.modalToken}>&#123;&#123;{f.key}&#125;&#125;</span>
                        </label>
                        <input
                          className={`${styles.modalInput}${f.computed ? ' '+styles.modalInputComputed : ''}`}
                          type="text"
                          readOnly={!!f.computed}
                          placeholder={f.ph}
                          value={fillValues[f.key] || ""}
                          onChange={e => !f.computed && handleFillValueChange(f.key, e.target.value)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* ── RIGHT: live salary breakdown (always-visible sticky panel) ── */}
            <div className={styles.modalBreakdownPane}>
              <p className={styles.breakdownPaneTitle}>
                📊 Salary Breakdown
                <span className={styles.breakdownLiveBadge}>LIVE</span>
              </p>

              {fillValues.S_BASIC_M ? (
                <>
                  <table className={styles.breakdownTable}>
                    <thead>
                      <tr>
                        <th>Component</th>
                        <th>Monthly (₹)</th>
                        <th>Yearly (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Basic',              fillValues.S_BASIC_M, fillValues.S_BASIC_A],
                        ['HRA (40% Basic)',    fillValues.S_HRA_M,   fillValues.S_HRA_A],
                        ['LTA',               fillValues.S_LTA_M,   fillValues.S_LTA_A],
                        ['Medical',           fillValues.S_MED_M,   fillValues.S_MED_A],
                        ['Tel. & Internet',   fillValues.S_TEL_M,   fillValues.S_TEL_A],
                        ['Special (Residual)', fillValues.S_SPEC_M, fillValues.S_SPEC_A],
                      ].map(([label, m, a]) => (
                        <tr key={label}>
                          <td>{label}</td>
                          <td>{m}</td>
                          <td>{a}</td>
                        </tr>
                      ))}
                      <tr className={styles.breakdownTotalRow}>
                        <td>Fixed Gross/mo</td>
                        <td>{fillValues.S_GROSS_M}</td>
                        <td>{fillValues.S_GROSS_A}</td>
                      </tr>
                      <tr className={fillValues.S_VARIABLE_A && fillValues.S_VARIABLE_A !== '—' ? styles.breakdownVariableRow : styles.breakdownNaRow}>
                        <td>Variable Pay (KPI)</td>
                        <td>{fillValues.S_VARIABLE_A !== '—' ? fillValues.S_VARIABLE_M : '—'}</td>
                        <td>{fillValues.S_VARIABLE_A || '—'}</td>
                      </tr>
                      <tr className={fillValues.S_ESOP_POOL && fillValues.S_ESOP_POOL !== '—' ? styles.breakdownEsopRow : styles.breakdownNaRow}>
                        <td>Retention Bonus</td>
                        <td>—</td>
                        <td>{fillValues.S_ESOP_POOL && fillValues.S_ESOP_POOL !== '—' ? fillValues.S_ESOP_POOL : '—'}</td>
                      </tr>
                      <tr className={styles.breakdownRelocRow}>
                        <td>+ Joining Bonus</td>
                        <td>—</td>
                        <td>{fillValues.S_RELOC_A}</td>
                      </tr>
                      <tr className={styles.breakdownCtcRow}>
                        <td>Total CTC</td>
                        <td>—</td>
                        <td>{fillValues.S_CTC1_A}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className={styles.breakdownNote}>
                    <strong>Bond Breach (Training &amp; Content Indemnity, max):</strong> {parseCTCNumber(fillValues.S_GROSS_M) > 0 ? `₹${fmtINR(parseCTCNumber(fillValues.S_GROSS_M) * 3)}` : '—'} (3 mo. Gross Monthly Salary; full amount payable on breach)<br/>
                    {fillValues.S_ESOP_POOL && fillValues.S_ESOP_POOL !== '—' && <><strong>Retention Bonus total pool:</strong> ₹{fillValues.S_ESOP_POOL} | Chunk 1: ₹{fillValues.S_ESOP_T1} | Chunk 2: ₹{fillValues.S_ESOP_T2} | Chunk 3: ₹{fillValues.S_ESOP_T3} | Chunk 4: ₹{fillValues.S_ESOP_T4} | Chunk 5: ₹{fillValues.S_ESOP_T5}<br/></>}
                    HRA exempt cap = 40% Basic (non-metro Lucknow) u/s 10(13A) · LTA twice/4-yr block u/s 10(5)
                  </p>
                  {parseCTCNumber(fillValues.GROSS_INPUT) > 0 && parseCTCNumber(fillValues.GROSS_INPUT) < 21000 && (
                    <p className={styles.breakdownNote} style={{ color: '#b45309', fontWeight: 600 }}>
                      ⚠️ ESIC applicable — monthly gross is at or below the ₹21,000 wage ceiling (ESIC Act, 1948).
                    </p>
                  )}
                </>
              ) : (
                <div className={styles.breakdownEmpty}>
                  <span className={styles.breakdownEmptyIcon}>💰</span>
                  <span>Enter <strong>CTC</strong> in the Compensation section to see the auto-computed salary breakdown here.</span>
                </div>
              )}
            </div>

          </div>

          <div className={styles.modalFooter}>
            <p className={styles.modalHint}>
              ⓘ Only filled fields will be replaced. Leave blank to keep the placeholder.
            </p>
            <div className={styles.modalActions}>
              <button className={styles.modalCancel} onClick={() => setShowFillModal(false)}>Cancel</button>
              <button className={styles.modalSubmit} onClick={handleFillSubmit}>
                <FaCheckCircle /> Apply to Letter
              </button>
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default OfferLetterEditor;